{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2019 - 2023                               }
{            Email : info@tmssoftware.com                            }
{            Web : https://www.tmssoftware.com                       }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.TMSFNCTrackBar;

{$I WEBLib.TMSFNCDefines.inc}

{$IFDEF WEBLIB}
{$DEFINE CMNWEBLIB}
{$ENDIF}
{$IFDEF CMNLIB}
{$DEFINE CMNWEBLIB}
{$ENDIF}

interface

uses
  Classes,
  {$IFNDEF LCLLIB}
  Types,
  {$ENDIF}
  {$IFDEF FMXLIB}
  FMX.Types,
  {$ENDIF}
  {$IFDEF CMNLIB}
  ExtCtrls,
  {$ENDIF}
  {$IFDEF WEBLIB}
  WEBLib.Controls, WEBLib.ExtCtrls,
  {$ENDIF}
  WEBLib.TMSFNCCustomControl, WEBLib.TMSFNCGraphics, WEBLib.TMSFNCTypes,
  WEBLib.TMSFNCGraphicsTypes, WEBLib.TMSFNCEdit, WEBLib.TMSFNCStyles;

const
  MAJ_VER = 1; // Major version nr.
  MIN_VER = 0; // Minor version nr.
  REL_VER = 6; // Release nr.
  BLD_VER = 1; // Build nr.

  //Version history
  //v1.0.0.0 : First Release
  //v1.0.0.1 : Fixed : Issue with control color in disabled state
  //v1.0.0.2 : Fixed : Issue with background color
  //v1.0.1.0 : New : Appearance.TrackLabelFont and Appearance.TickMarkLabelFont properties
  //v1.0.2.0 : New : Property Appearance.LineRightFill and Appearance.LineRightStroke added
  //v1.0.2.1 : Fixed : Issue in Delphi 11 with begin and end scene for CreateBitmapCanvas
  //v1.0.3.0 : New : Support for high dpi
  //v1.0.4.0 : New : Added Interaction.JumpToExactValue to enable better slider handling
  //v1.0.5.0 : New : GlobalFont interface implemented
  //v1.0.5.1 : Fixed : Issue with Interaction property
  //v1.0.5.2 : Fixed : Renamed ButtonShape bsNone value to bsNoButton to avoid conflicts
  //v1.0.6.0 : New : Added OnValueChange event
  //v1.0.6.1 : Fixed : Compilation error in Lazarus

type
  TTMSFNCCustomTrackBarBaseValues = record
    Left: Single;
    Right: Single;
  end;

  TTMSFNCTrackBarAppearanceTickMarkPosition = (tmpBoth, tmpBottomRight, tmpTopLeft, tmpNone);
  TTMSFNCTrackBarAppearanceButtonShape = (bsRounded, bsRectangle, bsImage, bsNoButton);
  TTMSFNCCustomTrackBarTrackLabelPosition = (tlpTopLeft, tlpTopCenter, tlpTopRight, tlpBottomLeft, tlpBottomCenter, tlpBottomRight, tlpNone);
  TTMSFNCCustomTrackBarBaseShape = (tsRectangle, tsEllipse, tsImage);
  TTMSFNCCustomTrackBarBaseLineShape = (lsRectangle, lsRoundedRectangle, lsImage);
  TTMSFNCCustomTrackBarOrientation = (tboHorizontal, tboVertical);

  TTMSFNCCustomTrackBarBaseAppearance = class(TPersistent)
  private
    FOwner: TComponent;
    FButtonStroke: TTMSFNCGraphicsStroke;
    FButtonFill: TTMSFNCGraphicsFill;
    FButtonSize: Integer;
    FOnChange: TNotifyEvent;
    FButtonSpacing: Integer;
    FThumbFill: TTMSFNCGraphicsFill;
    FThumbRightFill: TTMSFNCGraphicsFill;
    FThumbHeight: Integer;
    FThumbRightHeight: Integer;
    FThumbStroke: TTMSFNCGraphicsStroke;
    FThumbRightStroke: TTMSFNCGraphicsStroke;
    FThumbWidth: Integer;
    FThumbRightWidth: Integer;
    FLineWidth: Integer;
    FTickMarkSpacing: Integer;
    FTickMarkSize: Integer;
    FTickMarkStroke: TTMSFNCGraphicsStroke;
    FLineFill: TTMSFNCGraphicsFill;
    FLineStroke: TTMSFNCGraphicsStroke;
    FButtonShape: TTMSFNCTrackBarAppearanceButtonShape;
    FButtonDownFill: TTMSFNCGraphicsFill;
    FButtonDownStroke: TTMSFNCGraphicsStroke;
    FThumbDownFill: TTMSFNCGraphicsFill;
    FThumbRightDownFill: TTMSFNCGraphicsFill;
    FThumbDownStroke: TTMSFNCGraphicsStroke;
    FThumbRightDownStroke: TTMSFNCGraphicsStroke;
    FTickMarkPosition: TTMSFNCTrackBarAppearanceTickMarkPosition;
    FValueFont: TTMSFNCGraphicsFont;
    FThumbShape: TTMSFNCCustomTrackBarBaseShape;
    FTickMarkLabelFormat: string;
    FTrackLabelFormat: string;
    FTrackLabelPosition: TTMSFNCCustomTrackBarTrackLabelPosition;
    FTickMarkLabel: Boolean;
    FTickMarkDivision: Integer;
    FOrientation: TTMSFNCCustomTrackBarOrientation;
    FMinButtonBitmap: TTMSFNCBitmap;
    FMinButtonDownBitmap: TTMSFNCBitmap;
    FPlusButtonBitmap: TTMSFNCBitmap;
    FPlusButtonDownBitmap: TTMSFNCBitmap;
    FLineBitmap: TTMSFNCBitmap;
    FLineShape: TTMSFNCCustomTrackBarBaseLineShape;
    FThumbBitmap: TTMSFNCBitmap;
    FThumbDownBitmap: TTMSFNCBitmap;
    FThumbRightBitmap: TTMSFNCBitmap;
    FThumbRightDownBitmap: TTMSFNCBitmap;
    FTickMarkLabelFont: TTMSFNCGraphicsFont;
    FTrackLabelFont: TTMSFNCGraphicsFont;
    FLineRightStroke: TTMSFNCGraphicsStroke;
    FLineRightFill: TTMSFNCGraphicsFill;
    FLineMiddleFill: TTMSFNCGraphicsFill;
    FLineMiddleStroke: TTMSFNCGraphicsStroke;
    procedure SetButtonFill(const AValue: TTMSFNCGraphicsFill);
    procedure SetButtonSize(const AValue: Integer);
    procedure SetButtonStroke(const AValue: TTMSFNCGraphicsStroke);
    procedure SetButtonSpacing(const AValue: Integer);
    procedure SetThumbFill(const AValue: TTMSFNCGraphicsFill);
    procedure SetThumbHeight(const AValue: Integer);
    procedure SetThumbRightFill(const AValue: TTMSFNCGraphicsFill);
    procedure SetThumbRightHeight(const AValue: Integer);
    procedure SetThumbRightStroke(const AValue: TTMSFNCGraphicsStroke);
    procedure SetThumbRightWidth(const AValue: Integer);
    procedure SetThumbStroke(const AValue: TTMSFNCGraphicsStroke);
    procedure SetThumbWidth(const AValue: Integer);
    procedure SetLineWidth(const AValue: Integer);
    procedure SetTickMarkSpacing(const AValue: Integer);
    procedure SetTickMarkSize(const AValue: Integer);
    procedure SetLineFill(const AValue: TTMSFNCGraphicsFill);
    procedure SetLineStroke(const AValue: TTMSFNCGraphicsStroke);
    procedure SetTickMarkStroke(const AValue: TTMSFNCGraphicsStroke);
    procedure SetButtonShape(const AValue: TTMSFNCTrackBarAppearanceButtonShape);
    procedure SetButtonDownFill(const AValue: TTMSFNCGraphicsFill);
    procedure SetButtonDownStroke(const AValue: TTMSFNCGraphicsStroke);
    procedure SetThumbDownFill(const AValue: TTMSFNCGraphicsFill);
    procedure SetThumbDownStroke(const AValue: TTMSFNCGraphicsStroke);
    procedure SetThumbRightDownFill(const AValue: TTMSFNCGraphicsFill);
    procedure SetThumbRightDownStroke(const AValue: TTMSFNCGraphicsStroke);
    procedure SetTickMarkPosition(const AValue: TTMSFNCTrackBarAppearanceTickMarkPosition);
    procedure SetValueFont(const AValue: TTMSFNCGraphicsFont);
    procedure SetThumbShape(const AValue: TTMSFNCCustomTrackBarBaseShape);
    procedure SetTickMarkLabelFormat(const AValue: string);
    procedure SetTrackLabelFormat(const AValue: string);
    procedure SetTrackLabelPosition(const AValue: TTMSFNCCustomTrackBarTrackLabelPosition);
    procedure SetTickMarkLabel(const AValue: Boolean);
    procedure SetTickMarkDivision(const AValue: Integer);
    procedure SetOrientation(const AValue: TTMSFNCCustomTrackBarOrientation);
    function IsTickMarkLabelFormatStored: Boolean;
    function IsTrackLabelFormatStored: Boolean;
    procedure SetMinButtonBitmap(const Value: TTMSFNCBitmap);
    procedure SetMinButtonDownBitmap(const Value: TTMSFNCBitmap);
    procedure SetPlusButtonBitmap(const Value: TTMSFNCBitmap);
    procedure SetPlusButtonDownBitmap(const Value: TTMSFNCBitmap);
    procedure SetLineBitmap(const Value: TTMSFNCBitmap);
    procedure SetLineShape(const Value: TTMSFNCCustomTrackBarBaseLineShape);
    procedure SetThumbBitmap(const Value: TTMSFNCBitmap);
    procedure SetThumbDownBitmap(const Value: TTMSFNCBitmap);
    procedure SetThumbRightBitmap(const Value: TTMSFNCBitmap);
    procedure SetThumbRightDownBitmap(const Value: TTMSFNCBitmap);
    procedure SetTickMarkLabelFont(const Value: TTMSFNCGraphicsFont);
    procedure SetTrackLabelFont(const Value: TTMSFNCGraphicsFont);
    procedure SetLineRightStroke(const Value: TTMSFNCGraphicsStroke);
    procedure SetLineRightFill(const Value: TTMSFNCGraphicsFill);
    procedure SetLineMiddleFill(const Value: TTMSFNCGraphicsFill);
    procedure SetLineMiddleStroke(const Value: TTMSFNCGraphicsStroke);
  protected
    procedure Changed;
    procedure FillChanged(Sender: TObject);
    procedure StrokeChanged(Sender: TObject);
    procedure FontChanged(Sender: TObject);
    procedure BitmapChanged(Sender: TObject);
    property ButtonSize: Integer read FButtonSize write SetButtonSize default 20;
    property ButtonFill: TTMSFNCGraphicsFill read FButtonFill write SetButtonFill;
    property ButtonSpacing: Integer read FButtonSpacing write SetButtonSpacing default 2;
    property ButtonStroke: TTMSFNCGraphicsStroke read FButtonStroke write SetButtonStroke;
    property ButtonShape: TTMSFNCTrackBarAppearanceButtonShape read FButtonShape write SetButtonShape default bsNoButton;
    property ButtonDownFill: TTMSFNCGraphicsFill read FButtonDownFill write SetButtonDownFill;
    property ButtonDownStroke: TTMSFNCGraphicsStroke read FButtonDownStroke write SetButtonDownStroke;
    property MinButtonBitmap: TTMSFNCBitmap read FMinButtonBitmap write SetMinButtonBitmap;
    property MinButtonDownBitmap: TTMSFNCBitmap read FMinButtonDownBitmap write SetMinButtonDownBitmap;
    property PlusButtonBitmap: TTMSFNCBitmap read FPlusButtonBitmap write SetPlusButtonBitmap;
    property PlusButtonDownBitmap: TTMSFNCBitmap read FPlusButtonDownBitmap write SetPlusButtonDownBitmap;
    property ThumbBitmap: TTMSFNCBitmap read FThumbBitmap write SetThumbBitmap;
    property ThumbFill: TTMSFNCGraphicsFill read FThumbFill write SetThumbFill;
    property ThumbStroke: TTMSFNCGraphicsStroke read FThumbStroke write SetThumbStroke;
    property ThumbHeight: Integer read FThumbHeight write SetThumbHeight default 16;
    property ThumbWidth: Integer read FThumbWidth write SetThumbWidth default 8;
    property ThumbShape: TTMSFNCCustomTrackBarBaseShape read FThumbShape write SetThumbShape default tsRectangle;
    property ThumbDownBitmap: TTMSFNCBitmap read FThumbDownBitmap write SetThumbDownBitmap;
    property ThumbDownFill: TTMSFNCGraphicsFill read FThumbDownFill write SetThumbDownFill;
    property ThumbDownStroke: TTMSFNCGraphicsStroke read FThumbDownStroke write SetThumbDownStroke;
    property ThumbRightBitmap: TTMSFNCBitmap read FThumbRightBitmap write SetThumbRightBitmap;
    property ThumbRightFill: TTMSFNCGraphicsFill read FThumbRightFill write SetThumbRightFill;
    property ThumbRightStroke: TTMSFNCGraphicsStroke read FThumbRightStroke write SetThumbRightStroke;
    property ThumbRightHeight: Integer read FThumbRightHeight write SetThumbRightHeight default 16;
    property ThumbRightWidth: Integer read FThumbRightWidth write SetThumbRightWidth default 8;
    property ThumbRightDownBitmap: TTMSFNCBitmap read FThumbRightDownBitmap write SetThumbRightDownBitmap;
    property ThumbRightDownFill: TTMSFNCGraphicsFill read FThumbRightDownFill write SetThumbRightDownFill;
    property ThumbRightDownStroke: TTMSFNCGraphicsStroke read FThumbRightDownStroke write SetThumbRightDownStroke;
    property LineWidth: Integer read FLineWidth write SetLineWidth default 1;
    property LineShape: TTMSFNCCustomTrackBarBaseLineShape read FLineShape write SetLineShape default lsRectangle;
    property LineStroke: TTMSFNCGraphicsStroke read FLineStroke write SetLineStroke;
    property LineBitmap: TTMSFNCBitmap read FLineBitmap write SetLineBitmap;
    property LineFill: TTMSFNCGraphicsFill read FLineFill write SetLineFill;
    property LineRightFill: TTMSFNCGraphicsFill read FLineRightFill write SetLineRightFill;
    property LineRightStroke: TTMSFNCGraphicsStroke read FLineRightStroke write SetLineRightStroke;
    property LineMiddleFill: TTMSFNCGraphicsFill read FLineMiddleFill write SetLineMiddleFill;
    property LineMiddleStroke: TTMSFNCGraphicsStroke read FLineMiddleStroke write SetLineMiddleStroke;
    property Orientation: TTMSFNCCustomTrackBarOrientation read FOrientation write SetOrientation default tboHorizontal;
    property TickMarkSize: Integer read FTickMarkSize write SetTickMarkSize default 4;
    property TickMarkSpacing: Integer read FTickMarkSpacing write SetTickMarkSpacing default 8;
    property TickMarkStroke: TTMSFNCGraphicsStroke read FTickMarkStroke write SetTickMarkStroke;
    property TickMarkDivision: Integer read FTickMarkDivision write SetTickMarkDivision default 5;
    property TickMarkPosition: TTMSFNCTrackBarAppearanceTickMarkPosition read FTickMarkPosition write SetTickMarkPosition default tmpBottomRight;
    property TickMarkLabel: Boolean read FTickMarkLabel write SetTickMarkLabel default False;
    property TickMarkLabelFont: TTMSFNCGraphicsFont read FTickMarkLabelFont write SetTickMarkLabelFont;
    property TickMarkLabelFormat: string read FTickMarkLabelFormat write SetTickMarkLabelFormat stored IsTickMarkLabelFormatStored nodefault;
    property TrackLabelPosition: TTMSFNCCustomTrackBarTrackLabelPosition read FTrackLabelPosition write SetTrackLabelPosition default tlpNone;
    property TrackLabelFormat: string read FTrackLabelFormat write SetTrackLabelFormat stored IsTrackLabelFormatStored nodefault;
    property TrackLabelFont: TTMSFNCGraphicsFont read FTrackLabelFont write SetTrackLabelFont;
    property ValueFont: TTMSFNCGraphicsFont read FValueFont write SetValueFont;
  public
    constructor Create(AOwner: TComponent);
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  end;

  TTMSFNCCustomTrackBarAppearance = class(TTMSFNCCustomTrackBarBaseAppearance)
  published
    property ButtonSize;
    property ButtonFill;
    property ButtonSpacing;
    property ButtonStroke;
    property ButtonShape;
    property MinButtonBitmap;
    property MinButtonDownBitmap;
    property PlusButtonBitmap;
    property PlusButtonDownBitmap;
    property ButtonDownFill;
    property ButtonDownStroke;
    property ThumbBitmap;
    property ThumbFill;
    property ThumbStroke;
    property ThumbHeight;
    property ThumbWidth;
    property ThumbDownBitmap;
    property ThumbDownFill;
    property ThumbDownStroke;
    property ThumbShape;
    property LineBitmap;
    property LineShape;
    property LineWidth;
    property LineStroke;
    property LineFill;
    property LineRightFill;
    property LineRightStroke;
    property Orientation;
    property TickMarkPosition;
    property TickMarkSize;
    property TickMarkSpacing;
    property TickMarkStroke;
    property TickMarkLabelFont;
    property TickMarkLabelFormat;
    property TickMarkLabel;
    property TickMarkDivision;
    property TrackLabelFormat;
    property TrackLabelFont;
    property TrackLabelPosition;
  end;

  TTMSFNCCustomTrackBarBaseInteraction = class(TPersistent)
  private
    FOwner: TComponent;
    FFrequency: Single;
    FOnChange: TNotifyEvent;
    FEditable: Boolean;
    FRepeatClick: Boolean;
    FRepeatInterval: Integer;
    FJumpToExact: Boolean;
    procedure SetFrequency(const AValue: Single);
    procedure SetEditable(const AValue: Boolean);
    procedure SetRepeatClick(const AValue: Boolean);
    procedure SetRepeatInterval(const AValue: Integer);
    function IsFrequencyStored: Boolean;
    procedure SetJumpToExact(const Value: Boolean);
  protected
    procedure Changed;
    property JumpToExactValue: Boolean read FJumpToExact write SetJumpToExact default False;
    property Frequency: Single read FFrequency write SetFrequency stored IsFrequencyStored nodefault;
    property Editable: Boolean read FEditable write SetEditable default True;
    property RepeatClick: Boolean read FRepeatClick write SetRepeatClick default False;
    property RepeatInterval: Integer read FRepeatInterval write SetRepeatInterval default 100;
  public
    constructor Create(AOwner: TComponent);
    procedure Assign(Source: TPersistent); override;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  end;

  TTMSFNCCustomTrackBarInteraction = class(TTMSFNCCustomTrackBarBaseInteraction)
  published
    property Frequency;
    property RepeatClick;
    property RepeatInterval;
    property JumpToExactValue;
  end;

  TTMSFNCCustomTrackBarBaseBeforeDrawButtonEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; APlus: Boolean; var AAllow: Boolean; var ADefaultDraw: Boolean) of object;
  TTMSFNCCustomTrackBarBaseAfterDrawButtonEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; APlus: Boolean) of object;
  TTMSFNCCustomTrackBarBaseBeforeDrawTrackLine = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; var AAllow: Boolean; var ADefaultDraw: Boolean) of object;
  TTMSFNCCustomTrackBarBaseAfterDrawTrackLine = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF) of object;
  TTMSFNCCustomTrackBarBaseBeforeDrawTickMarks = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; var AAllow: Boolean; var ADefaultDraw: Boolean) of object;
  TTMSFNCCustomTrackBarBaseAfterDrawTickMarks = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF) of object;
  TTMSFNCCustomTrackBarBaseValueChanged = procedure(Sender: TObject; AValue: Single; ALeft: Boolean) of object;
  TTMSFNCCustomTrackBarBaseBeforeDrawTickLabel = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; APosition: TPointF; AValue: Single; var AAllow: Boolean; var ADefaultDraw: Boolean) of object;
  TTMSFNCCustomTrackBarBaseAfterDrawTickLabel = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; APosition: TPointF; AValue: Single) of object;
  TTMSFNCCustomTrackBarBaseBeforeDrawTrackLabel = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TrectF; ALabel: string; var AAllow: Boolean; var ADefaultDraw: Boolean) of object;
  TTMSFNCCustomTrackBarBaseAfterDrawTrackLabel = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; ALabel: string) of object;

  TTMSFNCCustomTrackBarBase = class(TTMSFNCCustomControl)
  private
    FMax: Single;
    FMin: Single;
    FMouseDown: Boolean;
    FPlusDown: Boolean;
    FMinusDown: Boolean;
    FOldButtonColor: TTMSFNCGraphicsColor;
    FOldThumbColor: TTMSFNCGraphicsColor;
    FOldThumbRightColor: TTMSFNCGraphicsColor;
    FOldLineColor: TTMSFNCGraphicsColor;
    FOldTickMarkColor: TTMSFNCGraphicsColor;
    FOldButtonDownColor: TTMSFNCGraphicsColor;
    FOldThumbDownColor: TTMSFNCGraphicsColor;
    FOldThumbRightDownColor: TTMSFNCGraphicsColor;
    FOldButtonFill: TTMSFNCGraphicsColor;
    FOldThumbFill: TTMSFNCGraphicsColor;
    FOldThumbRightFill: TTMSFNCGraphicsColor;
    FOldLineFill: TTMSFNCGraphicsColor;
    FOldButtonDownFill: TTMSFNCGraphicsColor;
    FOldThumbDownFill: TTMSFNCGraphicsColor;
    FOldThumbRightDownFill: TTMSFNCGraphicsColor;
    FOnBeforeDrawButton: TTMSFNCCustomTrackBarBaseBeforeDrawButtonEvent;
    FOnAfterDrawTrackLine: TTMSFNCCustomTrackBarBaseAfterDrawTrackLine;
    FOnAfterDrawTickMarks: TTMSFNCCustomTrackBarBaseAfterDrawTickMarks;
    FOnAfterDrawButton: TTMSFNCCustomTrackBarBaseAfterDrawButtonEvent;
    FOnBeforeDrawTrackLine: TTMSFNCCustomTrackBarBaseBeforeDrawTrackLine;
    FOnBeforeDrawTickMarks: TTMSFNCCustomTrackBarBaseBeforeDrawTickMarks;
    FOnChanged: TNotifyEvent;
    FOnValuesChanged: TTMSFNCCustomTrackBarBaseValueChanged;
    FOnBeforeDrawTickLabel: TTMSFNCCustomTrackBarBaseBeforeDrawTickLabel;
    FOnAfterDrawTickLabel: TTMSFNCCustomTrackBarBaseAfterDrawTickLabel;
    FOnBeforeDrawTrackLabel: TTMSFNCCustomTrackBarBaseBeforeDrawTrackLabel;
    FOnAfterDrawTrackLabel: TTMSFNCCustomTrackBarBaseAfterDrawTrackLabel;
    FOnValueChange: TTMSFNCCustomTrackBarBaseValueChanged;
    procedure SetMax(const AValue: Single); virtual;
    procedure SetMin(const AValue: Single);
    procedure SetAppearance(const AValue: TTMSFNCCustomTrackBarBaseAppearance);
    procedure SetInteraction(const AValue: TTMSFNCCustomTrackBarBaseInteraction);
    procedure SetValues(const AValue: TTMSFNCCustomTrackBarBaseValues);
    function IsMaxStored: Boolean;
    function IsMinStored: Boolean;
  protected
    FLeftSlider: Boolean;
    FPrevValue: Single;
    FRightSlider: Boolean;
    FValues: TTMSFNCCustomTrackBarBaseValues;
    FAppearance: TTMSFNCCustomTrackBarBaseAppearance;
    FInteraction: TTMSFNCCustomTrackBarBaseInteraction;
    {$IFDEF FMXLIB}
    procedure SetEnabled(const Value: Boolean); override;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    procedure SetEnabled(Value: Boolean); override;
    {$ENDIF}
    procedure ApplyStyle; override;
    procedure ResetToDefaultStyle; override;
    procedure AppearanceChanged(Sender: TObject);
    procedure InteractionChanged(Sender: TObject);
    procedure DoChanged; virtual;
    procedure Draw({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); override;
    procedure DoBeforeDrawButton({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF; APlus: Boolean; var AAllow: Boolean; var ADefaultDraw: Boolean); virtual;
    procedure DoAfterDrawButton({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF; APlus: Boolean); virtual;
    procedure DoBeforeDrawTrackLine({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF; var AAllow: Boolean; var ADefaultDraw: Boolean); virtual;
    procedure DoAfterDrawTrackLine({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); virtual;
    procedure DoBeforeDrawTickMarks({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF; var AAllow: Boolean; var ADefaultDraw: Boolean); virtual;
    procedure DoAfterDrawTickMarks({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); virtual;
    procedure DoBeforeDrawTickLabel({%H-}AGraphics: TTMSFNCGraphics; APosition: TPointF; AValue: Single; var AAllow: Boolean; var ADefaultDraw: Boolean); virtual;
    procedure DoAfterDrawTickLabel({%H-}AGraphics: TTMSFNCGraphics; APosition: TPointF; AValue: Single); virtual;
    procedure DoBeforeDrawTrackLabel({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF; ALabel: string; var AAllow: Boolean; var ADefaultDraw: Boolean); virtual;
    procedure DoAfterDrawTrackLabel({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF; ALabel: string); virtual;
    procedure DoValuesChange(AValue: Single; ALeft: Boolean); virtual;
    procedure DoValuesChanged(AValue: Single; ALeft: Boolean); virtual;
    procedure HandleMouseDown({%H-}Button: TTMSFNCMouseButton; {%H-}Shift: TShiftState; {%H-}X, {%H-}Y: Single); override;
    procedure HandleMouseMove({%H-}Shift: TShiftState; {%H-}X, {%H-}Y: Single); override;
    procedure HandleMouseUp({%H-}Button: TTMSFNCMouseButton; {%H-}Shift: TShiftState; {%H-}X, {%H-}Y: Single); override;
    procedure DrawPlusButton({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); virtual;
    procedure DrawMinusButton({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); virtual;
    procedure DrawTrackLine({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); virtual;
    procedure DrawTickLines({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); virtual;
    procedure DrawTickLinesText({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF; ATopLeft: Boolean); virtual;
    procedure DrawTrackLabel({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); virtual;
    procedure DrawTrackSliders({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); virtual;
    procedure InternalValueCheck(X, Y: Single; AChange: Boolean);
    function ClosestTo(const ATarget, AFirstValue, ASecondValue: Single): Single;
    function GetMaxButtonRect: TRectF; virtual;
    function GetMinButtonRect: TRectF; virtual;
    function GetSliderRect(ALeft: Boolean): TRectF; virtual;
    function GetThumbWidth: Single; virtual;
    function GetTrackLineRect: TRectF; virtual;
    function GetTrackLineDrawRect(ARect: TRectF): TRectF;
    function GetTickTopLeftRect: TRectF;
    function GetTickBottomRightRect: TRectF;
    function GetTrackLabelRect: TrectF;
    function GetTrackLabelTopLeft(ATrackRect: TRectF; ATextHeight: Single): Single;
    function CalcValue(AX, AY: Single; ALeft: Boolean): Single;
    property Appearance: TTMSFNCCustomTrackBarBaseAppearance read FAppearance write SetAppearance;
    property Interaction: TTMSFNCCustomTrackBarBaseInteraction read FInteraction write SetInteraction;
    property Max: Single read FMax write SetMax stored IsMaxStored nodefault;
    property Min: Single read FMin write SetMin stored IsMinStored nodefault;
    property Values: TTMSFNCCustomTrackBarBaseValues read FValues write SetValues;
    property OnChanged: TNotifyEvent read FOnChanged write FOnChanged;
    property OnBeforeDrawButton: TTMSFNCCustomTrackBarBaseBeforeDrawButtonEvent read FOnBeforeDrawButton write FOnBeforeDrawButton;
    property OnAfterDrawButton: TTMSFNCCustomTrackBarBaseAfterDrawButtonEvent read FOnAfterDrawButton write FOnAfterDrawButton;
    property OnBeforeDrawTrackLine: TTMSFNCCustomTrackBarBaseBeforeDrawTrackLine read FOnBeforeDrawTrackLine write FOnBeforeDrawTrackLine;
    property OnAfterDrawTrackLine: TTMSFNCCustomTrackBarBaseAfterDrawTrackLine read FOnAfterDrawTrackLine write FOnAfterDrawTrackLine;
    property OnBeforeDrawTickMarks: TTMSFNCCustomTrackBarBaseBeforeDrawTickMarks read FOnBeforeDrawTickMarks write FOnBeforeDrawTickMarks;
    property OnAfterDrawTickMarks: TTMSFNCCustomTrackBarBaseAfterDrawTickMarks read FOnAfterDrawTickMarks write FOnAfterDrawTickMarks;
    property OnBeforeDrawTickLabel: TTMSFNCCustomTrackBarBaseBeforeDrawTickLabel read FOnBeforeDrawTickLabel write FOnBeforeDrawTickLabel;
    property OnAfterDrawTickLabel: TTMSFNCCustomTrackBarBaseAfterDrawTickLabel read FOnAfterDrawTickLabel write FOnAfterDrawTickLabel;
    property OnBeforeDrawTrackLabel: TTMSFNCCustomTrackBarBaseBeforeDrawTrackLabel read FOnBeforeDrawTrackLabel write FOnBeforeDrawTrackLabel;
    property OnAfterDrawTrackLabel: TTMSFNCCustomTrackBarBaseAfterDrawTrackLabel read FOnAfterDrawTrackLabel write FOnAfterDrawTrackLabel;
    property OnValueChanged: TTMSFNCCustomTrackBarBaseValueChanged read FOnValuesChanged write FOnValuesChanged;
    property OnValueChange: TTMSFNCCustomTrackBarBaseValueChanged read FOnValueChange write FOnValueChange;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
  end;

  TTMSFNCCustomTrackBarValueChangedEvent = procedure(Sender: TObject; AValue: Single) of object;
  TTMSFNCCustomTrackBarBeforeDrawThumbEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; AValue: Single; var AAllow: Boolean; var ADefaultDraw: Boolean) of object;
  TTMSFNCCustomTrackBarAfterDrawThumbEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; AValue: Single) of object;

  TTMSFNCCustomTrackBar = class(TTMSFNCCustomTrackBarBase, ITMSFNCAppearanceGlobalFont)
  private
    FOnValueChanged: TTMSFNCCustomTrackBarValueChangedEvent;
    FOnValueChanging: TTMSFNCCustomTrackBarValueChangedEvent;
    FOnBeforeDrawThumb: TTMSFNCCustomTrackBarBeforeDrawThumbEvent;
    FOnAfterDrawThumb: TTMSFNCCustomTrackBarAfterDrawThumbEvent;
    FTrackInteraction: TTMSFNCCustomTrackBarInteraction;
    FTrackAppearance: TTMSFNCCustomTrackBarAppearance;
    FRepeatTimer: TTimer;
    FTrackDown: Boolean;
    FToValue: Single;
    FGlobalFont: TTMSFNCAppearanceGlobalFont;
    procedure SetMax(const AValue: Single); override;
    procedure SetTrackInteraction(const AValue: TTMSFNCCustomTrackBarInteraction);
    procedure SetTrackAppearance(const AValue: TTMSFNCCustomTrackBarAppearance);
    function IsValueStored: Boolean;
    function GetValue: Single;
    procedure SetGlobalFont(const Value: TTMSFNCAppearanceGlobalFont);
  protected
    procedure SetValue(const AValue: Single); virtual;
    procedure ChangeDPIScale(M, D: Integer); override;
    procedure DrawTrackLine({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); override;
    procedure DrawTrackSliders({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF); override;
    procedure DoBeforeDrawThumb({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF; AValue: Single; var AAllow: Boolean; var ADefaultDraw: Boolean); virtual;
    procedure DoAfterDrawThumb({%H-}AGraphics: TTMSFNCGraphics; {%H-}ARect: TRectF; AValue: Single); virtual;
    procedure HandleMouseDown({%H-}Button: TTMSFNCMouseButton; {%H-}Shift: TShiftState; {%H-}X, {%H-}Y: Single); override;
    procedure HandleMouseMove({%H-}Shift: TShiftState; {%H-}X, {%H-}Y: Single); override;
    procedure HandleKeyDown(var Key: Word; Shift: TShiftState); override;
    procedure HandleMouseUp({%H-}Button: TTMSFNCMouseButton; {%H-}Shift: TShiftState; {%H-}X, {%H-}Y: Single); override;
    procedure HandleMouseWheel(Shift: TShiftState; WheelDelta: Integer; var Handled: Boolean); override;
    procedure DoValueChanged(AValue: Single); virtual;
    procedure DoValueChange(AValue: Single); virtual;
    procedure DoRepeatTimer(Sender: TObject);
    procedure DoValuesChange(AValue: Single; ALeft: Boolean); override;
    procedure DoValuesChanged(AValue: Single; ALeft: Boolean); override;
    procedure TrackInteractionChange(Sender: TObject);
    procedure TrackAppearanceChange(Sender: TObject);
    function GetMaxOfThumbAndTrackRect: TRectF;
    function GetMaxButtonRect: TRectF; override;
    function GetMinButtonRect: TRectF; override;
    function GetThumbWidth: Single; override;
    function GetTrackLineRect: TRectF; override;
    procedure SetFonts(ASetType: TTMSFNCAppearanceGlobalFontType); virtual;
    property GlobalFont: TTMSFNCAppearanceGlobalFont read FGlobalFont write SetGlobalFont;
    property Appearance: TTMSFNCCustomTrackBarAppearance read FTrackAppearance write SetTrackAppearance;
    property Interaction: TTMSFNCCustomTrackBarInteraction read FTrackInteraction write SetTrackInteraction;
    property Value: Single read GetValue write SetValue stored IsValueStored nodefault;
    property OnValueChanged: TTMSFNCCustomTrackBarValueChangedEvent read FOnValueChanged write FOnValueChanged;
    property OnValueChange: TTMSFNCCustomTrackBarValueChangedEvent read FOnValueChanging write FOnValueChanging;
    property OnBeforeDrawThumb: TTMSFNCCustomTrackBarBeforeDrawThumbEvent read FOnBeforeDrawThumb write FOnBeforeDrawThumb;
    property OnAfterDrawThumb: TTMSFNCCustomTrackBarAfterDrawThumbEvent read FOnAfterDrawThumb write FOnAfterDrawThumb;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
    procedure DecreaseByFrequency;
    procedure EncreaseByFrequency;
  end;

  {$IFNDEF LCLLIB}
  [ComponentPlatformsAttribute(TMSPlatformsWeb)]
  {$ENDIF}
  TTMSFNCTrackBar = class(TTMSFNCCustomTrackBar)
  published
    property Appearance;
    property Interaction;
    property Fill;
    property Stroke;
    property Max;
    property Min;
    property Value;
    property GlobalFont;
    property OnChanged;
    property OnBeforeDrawButton;
    property OnAfterDrawButton;
    property OnBeforeDrawTrackLine;
    property OnAfterDrawTrackLine;
    property OnBeforeDrawTickMarks;
    property OnAfterDrawTickMarks;
    property OnBeforeDrawTickLabel;
    property OnAfterDrawTickLabel;
    property OnBeforeDrawTrackLabel;
    property OnAfterDrawTrackLabel;
    property OnBeforeDrawThumb;
    property OnAfterDrawThumb;
    property OnValueChanged;
    property OnValueChange;
  end;

const
  SLIDERLINESPACING = 4;
  SLIDERLINEROUNDING = 3;

implementation

uses
  SysUtils, Math, WEBLib.TMSFNCUtils
  {$IFDEF VCLLIB}
  , VCL.Graphics
  {$ENDIF};

{ TTMSFNCCustomTrackBarBase }

procedure TTMSFNCCustomTrackBarBase.AppearanceChanged(Sender: TObject);
begin
  DoChanged;
end;

procedure TTMSFNCCustomTrackBarBase.ApplyStyle;
var
  c: TTMSFNCGraphicsColor;
begin
  inherited;
  c := gcNull;
  if TTMSFNCStyles.GetStyleBackgroundFillColor(c) then
  begin
    Fill.Color := c;
    FAppearance.LineFill.Color := c;
  end;

  c := gcNull;
  if TTMSFNCStyles.GetStyleAlternativeBackgroundFillColor(c) then
  begin
    FAppearance.ButtonFill.Color := c;
    FAppearance.ThumbFill.Color := c;
    FAppearance.ThumbRightFill.Color := c;
  end;

  c := gcNull;
  if TTMSFNCStyles.GetStyleLineFillColor(c) then
  begin
    Stroke.Kind := gskNone;
    FAppearance.LineStroke.Color := c;
    FAppearance.ButtonStroke.Color := c;
    FAppearance.ThumbStroke.Color := c;
    FAppearance.ThumbDownStroke.Color := c;
    FAppearance.ThumbRightStroke.Color := c;
    FAppearance.ThumbRightDownStroke.Color := c;
    FAppearance.TickMarkStroke.Color := c;
  end;

  c := gcNull;
  if TTMSFNCStyles.GetStyleSelectionFillColor(c) then
  begin
    FAppearance.ButtonDownFill.Color := c;
    FAppearance.ThumbDownFill.Color := c;
    FAppearance.ThumbRightDownFill.Color := c;
  end;

  c := gcNull;
  if TTMSFNCStyles.GetStyleTextFontColor(c) then
  begin
    FAppearance.ValueFont.Color := c;
  end;
end;

function TTMSFNCCustomTrackBarBase.CalcValue(AX, AY: Single; ALeft: Boolean): Single;
var
  r: TRectF;
  d, w, tw: single;
begin
  Result := 0;
  d := FMax - FMin;
  if d = 0 then
    d := 1;

  r := GetTrackLineRect;
  tw := GetThumbWidth / 2;
  case FAppearance.Orientation of
    tboHorizontal:
    begin
      InflateRectEx(r, -tw, 0);
      w := r.Right - r.Left;
      if w = 0 then
        w := 1;
      Result := ((AX - r.Left) * d / w) + FMin;
    end;
    tboVertical:
    begin
      InflateRectEx(r, 0, -tw);
      w := r.Bottom - r.Top;
      if w = 0 then
        w := 1;
      Result := (d - ((AY - r.Top) * d / w)) + FMin;
    end;
  end;

  if ALeft then
  begin
    if Result < FMin then
      Result := FMin
    else if Result > FValues.Right then
      Result := FValues.Right;
  end
  else
  begin
    if Result < FValues.Left then
      Result := FValues.Left
    else if Result > FMax then
      Result := FMax;
  end;
end;

function TTMSFNCCustomTrackBarBase.ClosestTo(const ATarget, AFirstValue,
  ASecondValue: Single): Single;
begin
  if Abs(ATarget - AFirstValue) <= Abs(ATarget - ASecondValue) then
    Result := AFirstValue
  else
    Result := ASecondValue;
end;

constructor TTMSFNCCustomTrackBarBase.Create(AOwner: TComponent);
begin
  inherited;
  {$IFDEF CMNLIB}
  {$IFDEF MSWINDOWS}
  NativeCanvas := True;
  TextQuality := gtqClearType;
  {$ENDIF}
  {$ENDIF}
  FMin := 0;
  FMax := 100;
  FMouseDown := False;
  FPlusDown := False;
  FMinusDown := False;
  FAppearance := TTMSFNCCustomTrackBarBaseAppearance.Create(Self);
  FAppearance.OnChange := @AppearanceChanged;
  FInteraction := TTMSFNCCustomTrackBarBaseInteraction.Create(Self);
  FInteraction.OnChange := @InteractionChanged;
end;

destructor TTMSFNCCustomTrackBarBase.Destroy;
begin
  FAppearance.Free;
  FInteraction.Free;
  inherited;
end;

procedure TTMSFNCCustomTrackBarBase.DoAfterDrawButton(
  AGraphics: TTMSFNCGraphics; ARect: TRectF; APlus: Boolean);
begin
  if Assigned(OnAfterDrawButton) then
    OnAfterDrawButton(Self, AGraphics, AREct, APlus);
end;

procedure TTMSFNCCustomTrackBarBase.DoAfterDrawTickLabel(
  AGraphics: TTMSFNCGraphics; APosition: TPointF; AValue: Single);
begin
  if Assigned(OnAfterDrawTickLabel) then
    OnAfterDrawTickLabel(Self, AGraphics, APosition, AValue);
end;

procedure TTMSFNCCustomTrackBarBase.DoAfterDrawTickMarks(
  AGraphics: TTMSFNCGraphics; ARect: TRectF);
begin
  if Assigned(OnAfterDrawTickMarks) then
    OnAfterDrawTickMarks(Self, AGraphics, ARect);
end;

procedure TTMSFNCCustomTrackBarBase.DoAfterDrawTrackLabel(
  AGraphics: TTMSFNCGraphics; ARect: TRectF; ALabel: string);
begin
  if Assigned(OnAfterDrawTrackLabel) then
    OnAfterDrawTrackLabel(Self, AGraphics, ARect, ALabel);
end;

procedure TTMSFNCCustomTrackBarBase.DoAfterDrawTrackLine(
  AGraphics: TTMSFNCGraphics; ARect: TRectF);
begin
  if Assigned(OnAfterDrawTrackLine) then
    OnAfterDrawTrackLine(Self, AGraphics, ARect);
end;

procedure TTMSFNCCustomTrackBarBase.DoBeforeDrawButton(
  AGraphics: TTMSFNCGraphics; ARect: TRectF; APlus: Boolean; var AAllow,
  ADefaultDraw: Boolean);
begin
  if Assigned(OnBeforeDrawButton) then
    OnBeforeDrawButton(Self, AGraphics, ARect, APlus, AAllow, ADefaultDraw);
end;

procedure TTMSFNCCustomTrackBarBase.DoBeforeDrawTickLabel(
  AGraphics: TTMSFNCGraphics; APosition: TPointF; AValue: Single; var AAllow,
  ADefaultDraw: Boolean);
begin
  if Assigned(OnBeforeDrawTickLabel) then
    OnBeforeDrawTickLabel(Self, AGraphics, APosition, AValue, AAllow, ADefaultDraw);
end;

procedure TTMSFNCCustomTrackBarBase.DoBeforeDrawTickMarks(
  AGraphics: TTMSFNCGraphics; ARect: TRectF; var AAllow, ADefaultDraw: Boolean);
begin
  if Assigned(OnBeforeDrawTickMarks) then
    OnBeforeDrawTickMarks(Self, AGraphics, ARect, AAllow, ADefaultDraw);
end;

procedure TTMSFNCCustomTrackBarBase.DoBeforeDrawTrackLabel(
  AGraphics: TTMSFNCGraphics; ARect: TRectF; ALabel: string; var AAllow,
  ADefaultDraw: Boolean);
begin
  if Assigned(OnBeforeDrawTrackLabel) then
    OnBeforeDrawTrackLabel(Self, AGraphics, ARect, ALabel, AAllow, ADefaultDraw);
end;

procedure TTMSFNCCustomTrackBarBase.DoBeforeDrawTrackLine(
  AGraphics: TTMSFNCGraphics; ARect: TRectF; var AAllow, ADefaultDraw: Boolean);
begin
  if Assigned(OnBeforeDrawTrackLine) then
    OnBeforeDrawTrackLine(Self, AGraphics, ARect, AAllow, ADefaultDraw);
end;

procedure TTMSFNCCustomTrackBarBase.DoChanged;
begin
  if Assigned(OnChanged) then
    OnChanged(Self);

  Invalidate;
end;

procedure TTMSFNCCustomTrackBarBase.DoValuesChange(AValue: Single;
  ALeft: Boolean);
begin
  NotifyDataBinderEdit;
  NotifyDataBinderValueChanged;

  if Assigned(OnValueChange) then
    OnValueChange(Self, AValue, ALeft);
end;

procedure TTMSFNCCustomTrackBarBase.DoValuesChanged(AValue: Single;
  ALeft: Boolean);
begin
  NotifyDataBinderEdit;
  NotifyDataBinderValueChanged;

  if Assigned(OnValueChanged) then
    OnValueChanged(Self, AValue, ALeft);
end;

procedure TTMSFNCCustomTrackBarBase.Draw(AGraphics: TTMSFNCGraphics; ARect: TRectF);
var
  minBtnRect, maxBtnRect, trackRect: TRectF;
begin
  inherited;
  minBtnRect := GetMinButtonRect;
  DrawMinusButton(AGraphics, minBtnRect);
  maxBtnRect := GetMaxButtonRect;
  DrawPlusButton(AGraphics, maxBtnRect);

  trackRect := GetTrackLineRect;
  DrawTrackLine(AGraphics, GetTrackLineDrawRect(trackRect));
  trackRect := GetTickTopLeftRect;
  DrawTickLines(AGraphics, trackRect);
  DrawTickLinesText(AGraphics, trackRect, True);
  trackRect := GetTickBottomRightRect;
  DrawTickLines(AGraphics, trackRect);
  DrawTickLinesText(AGraphics, trackRect, False);

  trackRect := GetTrackLabelRect;
  DrawTrackLabel(AGraphics, trackRect);

  DrawTrackSliders(AGraphics, ARect);
end;

procedure TTMSFNCCustomTrackBarBase.DrawMinusButton(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
var
  l, hc, vc: Single;
  b, df: Boolean;
  bmp: TTMSFNCBitmap;
begin
  if FMinusDown then
  begin
    AGraphics.Fill.Assign(FAppearance.ButtonDownFill);
    AGraphics.Stroke.Assign(FAppearance.ButtonDownStroke);
    bmp := (FAppearance.MinButtonDownBitmap);
  end
  else
  begin
    AGraphics.Fill.Assign(FAppearance.ButtonFill);
    AGraphics.Stroke.Assign(FAppearance.ButtonStroke);
    bmp := (FAppearance.MinButtonBitmap);
  end;

  b := True;
  df := True;
  DoBeforeDrawButton(AGraphics, ARect, False, b, df);
  if b then
  begin
    if df then
    begin
      case FAppearance.ButtonShape of
        bsRounded: AGraphics.DrawEllipse(ARect);
        bsRectangle: AGraphics.DrawRectangle(ARect);
        bsImage:
        begin
          if Assigned(bmp) then
            AGraphics.DrawBitmap(ARect, bmp);
        end;
      end;

      if (FAppearance.ButtonShape <> bsImage) and (FAppearance.ButtonShape <> bsNoButton) then
      begin
        l := Math.Min((ARect.Right - ARect.Left), (ARect.Bottom - ARect.Top));
        hc := (ARect.Right - ARect.Left) / 2;
        vc := (ARect.Bottom - ARect.Top) / 2;
        AGraphics.DrawLine(PointF(ARect.Left + hc - l / 4, ARect.Top + vc), PointF(ARect.Left + hc + l / 4, ARect.Top + vc));
      end;
    end;

    DoAfterDrawbutton(AGraphics, ARect, False);
  end;
end;

procedure TTMSFNCCustomTrackBarBase.DrawPlusButton(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
var
  l, hc, vc: Single;
  b, df: Boolean;
  bmp: TTMSFNCBitmap;
begin
  if FPlusDown then
  begin
    AGraphics.Fill.Assign(FAppearance.ButtonDownFill);
    AGraphics.Stroke.Assign(FAppearance.ButtonDownStroke);
    bmp := FAppearance.PlusButtonDownBitmap;
  end
  else
  begin
    AGraphics.Fill.Assign(FAppearance.ButtonFill);
    AGraphics.Stroke.Assign(FAppearance.ButtonStroke);
    bmp := FAppearance.PlusButtonBitmap;
  end;

  b := True;
  df := True;
  DoBeforeDrawButton(AGraphics, ARect, True, b, df);
  if b then
  begin
    if df then
    begin
      case FAppearance.ButtonShape of
        bsRounded: AGraphics.DrawEllipse(ARect);
        bsRectangle: AGraphics.DrawRectangle(ARect);
        bsImage:
        begin
          if Assigned(bmp) then
            AGraphics.DrawBitmap(ARect, bmp);
        end;
      end;

      if (FAppearance.ButtonShape <> bsImage) and (FAppearance.ButtonShape <> bsNoButton) then
      begin
        l := Math.Min((ARect.Right - ARect.Left), (ARect.Bottom - ARect.Top));
        hc := (ARect.Right - ARect.Left) / 2;
        vc := (ARect.Bottom - ARect.Top) / 2;
        AGraphics.DrawLine(PointF(ARect.Left + hc - l / 4, ARect.Top + vc), PointF(ARect.Left + hc + l / 4, ARect.Top + vc));
        AGraphics.DrawLine(PointF(ARect.Left + hc, ARect.Top + vc - l / 4), PointF(ARect.Left + hc, ARect.Top + vc + l / 4));
      end;
    end;

    DoAfterDrawbutton(AGraphics, ARect, True);
  end;
end;

procedure TTMSFNCCustomTrackBarBase.DrawTickLines(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
var
  tr, b, l, w: Single;
  I: Integer;
  bf, df: Boolean;
begin
  if (FAppearance.TickMarkPosition = tmpNone) or (FAppearance.TickMarkDivision = 0) then
    Exit;

  w := 0;
  case FAppearance.Orientation of
    tboHorizontal: w := ARect.Right - ARect.Left;
    tboVertical: w := ARect.Bottom - ARect.Top;
  end;

  if w = 0 then
    Exit;

  w := w / FAppearance.TickMarkDivision;

  AGraphics.Stroke.Assign(FAppearance.TickMarkStroke);

  bf := True;
  df := True;
  DoBeforeDrawTickMarks(AGraphics, ARect, bf, df);
  begin
    if df then
    begin
      b := ARect.Bottom;
      l := ARect.Left;

      case FAppearance.Orientation of
        tboHorizontal:
        begin
          tr := ARect.Top;
          for I := 0 to FAppearance.TickMarkDivision do
          begin
            AGraphics.DrawLine(PointF(l, tr), PointF(l, b));
            l := l + w;
          end;
        end;
        tboVertical:
        begin
          tr := ARect.Right;
          for I := 0 to FAppearance.TickMarkDivision do
          begin
            AGraphics.DrawLine(PointF(l, b), PointF(tr, b));
            b := b - w;
          end;
        end;
      end;
    end;

    DoAfterDrawTickMarks(AGraphics, ARect);
  end;
end;

procedure TTMSFNCCustomTrackBarBase.DrawTickLinesText(
  AGraphics: TTMSFNCGraphics; ARect: TRectF; ATopLeft: Boolean);
var
  p: TPointF;
  w, h, val, tmp, tmspacing: Single;
  s: string;
  I, divn: Integer;
  b, df: Boolean;
begin
  if ((ARect.Right - ARect.Left = 0) and (ARect.Top - ARect.Bottom = 0)) or not FAppearance.TickMarkLabel then
    Exit;

  val := FMin;
  tmp := 0;
  divn := FAppearance.TickMarkDivision;
  tmspacing := 0;
  if ((ARect.Right - ARect.Left <> 0) and (ARect.Top - ARect.Bottom <> 0)) then
    tmspacing := SLIDERLINESPACING;

  if ATopLeft then
  begin
    if FAppearance.Orientation = tboHorizontal then
    begin
      if FAppearance.TickMarkPosition = tmpBottomRight then
        Exit;

      AGraphics.Font.AssignSource(Appearance.TickMarkLabelFont);
      for I := 0 to divn do
      begin
        s := Format(FAppearance.TickMarkLabelFormat, [val]);
        w := AGraphics.CalculateTextWidth(s) / 2;
        h := AGraphics.CalculateTextHeight(s);
        p := PointF(ARect.Left - w + tmp, ARect.Top - tmspacing - h);

        b := True;
        df := True;
        DoBeforeDrawTickLabel(AGraphics, p, val, b, df);
        if b then
        begin
          if df then
            AGraphics.DrawText(p, s);

          DoAfterDrawTickLabel(AGraphics, p, val);
        end;

        val := val + (FMax - FMin) / divn;
        tmp := tmp + (ARect.Right - ARect.Left) / divn;
      end;
    end
    else
    begin
      if FAppearance.TickMarkPosition = tmpBottomRight then
        Exit;

      AGraphics.Font.AssignSource(Appearance.TickMarkLabelFont);
      for I := 0 to divn do
      begin
        s := Format(FAppearance.TickMarkLabelFormat, [val]);
        w := AGraphics.CalculateTextWidth(s);
        h := AGraphics.CalculateTextHeight(s) / 2;
        p := PointF(ARect.Left - tmspacing - w, ARect.Bottom - h - tmp);

        b := True;
        df := True;
        DoBeforeDrawTickLabel(AGraphics, p, val, b, df);
        if b then
        begin
          if df then
            AGraphics.DrawText(p, s);

          DoAfterDrawTickLabel(AGraphics, p, val);
        end;

        val := val + (FMax - FMin) / divn;
        tmp := tmp + (ARect.Bottom - ARect.Top) / divn;
      end;
    end;
  end
  else
  begin
    if FAppearance.Orientation = tboHorizontal then
    begin
      if FAppearance.TickMarkPosition = tmpTopLeft then
        Exit;

      AGraphics.Font.AssignSource(Appearance.TickMarkLabelFont);
      for I := 0 to divn do
      begin
        s := Format(FAppearance.TickMarkLabelFormat, [val]);
        w := AGraphics.CalculateTextWidth(s) / 2;
        p := PointF(ARect.Left - w + tmp, ARect.Bottom + tmspacing);

        b := True;
        df := True;
        DoBeforeDrawTickLabel(AGraphics, p, val, b, df);
        if b then
        begin
          if df then
            AGraphics.DrawText(p, s);

          DoAfterDrawTickLabel(AGraphics, p, val);
        end;

        val := val + (FMax - FMin) / divn;
        tmp := tmp + (ARect.Right - ARect.Left) / divn;
      end;
    end
    else
    begin
      if FAppearance.TickMarkPosition = tmpTopLeft then
        Exit;

      AGraphics.Font.AssignSource(Appearance.TickMarkLabelFont);
      for I := 0 to divn do
      begin
        s := Format(FAppearance.TickMarkLabelFormat, [val]);
        h := AGraphics.CalculateTextHeight(s) / 2;
        p := PointF(ARect.Right + tmspacing, ARect.Bottom - h - tmp);

        b := True;
        df := True;
        DoBeforeDrawTickLabel(AGraphics, p, val, b, df);
        if b then
        begin
          if df then
            AGraphics.DrawText(p, s);

          DoAfterDrawTickLabel(AGraphics, p, val);
        end;

        val := val + (FMax - FMin) / divn;
        tmp := tmp + (ARect.Bottom - ARect.Top) / divn;
      end;
    end;
  end;
end;

procedure TTMSFNCCustomTrackBarBase.DrawTrackLabel(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
var
  s: string;
  b, df: Boolean;
begin
  if (ARect.Bottom - ARect.Top = 0) or (ARect.Right - ARect.Left = 0) then
    Exit;

  s := Format(FAppearance.TrackLabelFormat, [FValues.Left, FValues.Right]);
  AGraphics.Font.AssignSource(Appearance.TrackLabelFont);

  b := True;
  df := True;
  DoBeforeDrawTrackLabel(AGraphics, ARect, s, b, df);
  if b then
  begin
    if df then
    begin
      case FAppearance.Orientation of
        tboHorizontal: AGraphics.DrawText(PointF(ARect.Left, ARect.Top), s);
        tboVertical: AGraphics.DrawText(ARect, s, False, gtaLeading, gtaCenter, gttNone, -90);
      end;
    end;
    DoAfterDrawTrackLabel(AGraphics, ARect, s);
  end;
end;

procedure TTMSFNCCustomTrackBarBase.DrawTrackLine(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
begin
  //
end;

procedure TTMSFNCCustomTrackBarBase.DrawTrackSliders(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
begin
  //
end;

function TTMSFNCCustomTrackBarBase.GetMaxButtonRect: TRectF;
begin
  Result := RectF(0, 0, 0, 0);
end;

function TTMSFNCCustomTrackBarBase.GetMinButtonRect: TRectF;
begin
  Result := RectF(0, 0, 0, 0);
end;

function TTMSFNCCustomTrackBarBase.GetSliderRect(ALeft: Boolean): TRectF;
var
  x, y, sw, sh, tw: Single;
  r: TRectF;
begin
  if FMax - FMin <> 0 then
  begin
    if ALeft then
      x := (FValues.Left - FMin) / (FMax - FMin)
    else
      x := (FValues.Right - FMin) / (FMax - FMin);
  end
  else
    x := FMax;

  if ALeft then
  begin
    sw := FAppearance.ThumbWidth / 2;
    sh := FAppearance.ThumbHeight / 2;
  end
  else
  begin
    sw := FAppearance.ThumbRightWidth / 2;
    sh := FAppearance.ThumbRightHeight / 2;
  end;

  r := GetTrackLineRect;
  tw := GetThumbWidth / 2;
  case FAppearance.Orientation of
    tboHorizontal:
    begin
      InflateRectEx(r, -tw, 0);
      x := r.Left + (r.Right - r.Left) * x;
      y := r.Top + (r.Bottom - r.Top) / 2;
      Result := RectF(x - sw, y - sh, x + sw, y + sh);
    end;
    tboVertical:
    begin
      InflateRectEx(r, 0, -tw);
      x := r.Top + (r.Bottom - r.Top) * (1 - x);
      y := r.Left + (r.Right - r.Left) / 2;
      Result := RectF(y - sh, x - sw, y + sh, x + sw);
    end;
  end;
end;

function TTMSFNCCustomTrackBarBase.GetThumbWidth: Single;
begin
  Result := 0;
end;

function TTMSFNCCustomTrackBarBase.GetTickBottomRightRect: TRectF;
var
  r: TRectF;
  tw: Single;
begin
  Result := RectF(0, 0, 0, 0);
  r := GetTrackLineDrawRect(GetTrackLineRect);
  tw := GetThumbWidth / 2;
  case FAppearance.Orientation of
    tboHorizontal:
    begin
      InflateRectEx(r, -tw, 0);
      case FAppearance.TickMarkPosition of
        tmpBoth, tmpBottomRight: Result := RectF(r.Left, r.Bottom + FAppearance.TickMarkSpacing, r.Right, r.Bottom + FAppearance.TickMarkSpacing + FAppearance.TickMarkSize);
        tmpNone: Result := RectF(r.Left, r.Bottom + FAppearance.TickMarkSpacing, r.Right, r.Bottom + FAppearance.TickMarkSpacing);
      end;
    end;
    tboVertical:
    begin
      InflateRectEx(r, 0, -tw);
      case FAppearance.TickMarkPosition of
        tmpBoth, tmpBottomRight: Result := RectF(r.Right + FAppearance.TickMarkSpacing, r.Top, r.Right + FAppearance.TickMarkSpacing + FAppearance.TickMarkSize, r.Bottom);
        tmpNone: Result := RectF(r.Right + FAppearance.TickMarkSpacing, r.Top, r.Right + FAppearance.TickMarkSpacing, r.Bottom);
      end;
    end;
  end;
end;

function TTMSFNCCustomTrackBarBase.GetTickTopLeftRect: TRectF;
var
  r: TrectF;
  tw: Single;
begin
  Result := RectF(0, 0, 0, 0);
  r := GetTrackLineDrawRect(GetTrackLineRect);
  tw := GetThumbWidth / 2;
  case FAppearance.Orientation of
    tboHorizontal:
    begin
      InflateRectEx(r, -tw, 0);
      case FAppearance.TickMarkPosition of
        tmpBoth, tmpTopLeft: Result := RectF(r.Left, r.Top - FAppearance.TickMarkSpacing - FAppearance.TickMarkSize, r.Right, r.Top - FAppearance.TickMarkSpacing);
      end;
    end;
    tboVertical:
    begin
      InflateRectEx(r, 0, -tw);
      case FAppearance.TickMarkPosition of
        tmpBoth, tmpTopLeft: Result := RectF(r.Left - FAppearance.TickMarkSpacing - FAppearance.TickMarkSize, r.Top, r.Left - FAppearance.TickMarkSpacing, r.Bottom);
      end;
    end;
  end;
end;

function TTMSFNCCustomTrackBarBase.GetTrackLabelRect: TrectF;
var
  tl, tw, th, cp: Single;
  dr: TrectF;
  s: string;
  g: TTMSFNCGraphics;
begin
  Result := RectF(0, 0, 0, 0);
  if FAppearance.TrackLabelPosition = tlpNone then
    Exit;

  dr := GetTrackLineDrawRect(GetTrackLineRect);
  s := Format(FAppearance.TrackLabelFormat, [FValues.Left, FValues.Right]);

  g := TTMSFNCGraphics.CreateBitmapCanvas;
  g.Font.AssignSource(Appearance.TrackLabelFont);
  g.BeginScene;
  try
    tw := g.CalculateTextWidth(s);
    th := g.CalculateTextHeight(s);
  finally
    g.EndScene;
    g.Free;
  end;

  case FAppearance.Orientation of
    tboHorizontal:
    begin
      tl := GetTrackLabelTopLeft(dr, th);
      cp := dr.Left + (dr.Right - dr.Left) / 2;

      case FAppearance.TrackLabelPosition of
        tlpTopLeft, tlpBottomLeft: Result := RectF(dr.Left, tl, dr.Left + tw, tl + th);
        tlpTopCenter, tlpBottomCenter: Result := RectF(cp - tw / 2, tl, cp + tw / 2, tl + th);
        tlpTopRight, tlpBottomRight: Result := RectF(dr.Right - tw, tl, dr.Right, tl + th);
      end;
    end;
    tboVertical:
    begin
      tl := GetTrackLabelTopLeft(dr, th);
      cp := dr.Bottom - (dr.Bottom - dr.Top) / 2;

      case FAppearance.TrackLabelPosition of
        tlpTopLeft, tlpBottomLeft: Result := RectF(tl, dr.Bottom - tw, tl + th, dr.Bottom);
        tlpTopCenter, tlpBottomCenter: Result := RectF(tl, cp - tw / 2, tl + th, cp + tw / 2);
        tlpTopRight, tlpBottomRight: Result := RectF(tl, dr.Top, tl + th, dr.Top + tw);
      end;
    end;
  end;
end;

function TTMSFNCCustomTrackBarBase.GetTrackLabelTopLeft(ATrackRect: TRectF;
  ATextHeight: Single): Single;
var
  tl, br: Single;
  g: TTMSFNCGraphics;
begin
  Result := 0;

  tl := 0;
  br := 0;
  case FAppearance.Orientation of
    tboHorizontal:
    begin
      tl := ATrackRect.Top;
      br := ATrackRect.Bottom;
    end;
    tboVertical:
    begin
      tl := ATrackRect.Left;
      br := ATrackRect.Right;
    end;
  end;

  case FAppearance.TrackLabelPosition of
    tlpTopLeft, tlpTopCenter, tlpTopRight:
    begin
      br := 0;
      Result := tl - SLIDERLINESPACING - ATextHeight;

      if (FAppearance.TickMarkPosition <> tmpNone) and (FAppearance.TickMarkPosition <> tmpBottomRight) then
        br := Result - FAppearance.TickMarkSpacing - FAppearance.TickMarkSize;

      if br <> 0 then
        Result := br;

      if FAppearance.TickMarkLabel and (br <> 0) then
      begin
        g := TTMSFNCGraphics.CreateBitmapCanvas;
        g.BeginScene;
        try
          br := g.CalculateTextWidth(Format(FAppearance.TickMarkLabelFormat, [FMax]));
        finally
          g.EndScene;
          g.Free;
        end;
        Result := Result - br - SLIDERLINESPACING;
      end;
    end;
    tlpBottomLeft, tlpBottomCenter, tlpBottomRight:
    begin
      tl := 0;
      Result := br + SLIDERLINESPACING;

      if (FAppearance.TickMarkPosition <> tmpNone) and (FAppearance.TickMarkPosition <> tmpTopLeft) then
        tl := Result + FAppearance.TickMarkSpacing + FAppearance.TickMarkSize;

      if tl <> 0 then
        Result := tl;

      if FAppearance.TickMarkLabel and (tl <> 0) then
      begin
        g := TTMSFNCGraphics.CreateBitmapCanvas;
        g.BeginScene;
        try
          tl := g.CalculateTextWidth(Format(FAppearance.TickMarkLabelFormat, [FMax]));
        finally
          g.EndScene;
          g.Free;
        end;
        Result := Result + tl + SLIDERLINESPACING;
      end;
    end;
  end;
end;

function TTMSFNCCustomTrackBarBase.GetTrackLineDrawRect(ARect: TRectF): TRectF;
var
  h, w: single;
begin
  Result := RectF(0, 0, 0, 0);
  w := FAppearance.LineWidth / 2;
  case FAppearance.Orientation of
    tboHorizontal:
    begin
      h := (ARect.Bottom - ARect.Top) / 2;
      Result := RectF(ARect.Left, ARect.Top + h - w, ARect.Right, ARect.Top + h + w);
    end;
    tboVertical:
    begin
      h := (ARect.Right - ARect.Left) / 2;
      Result := RectF(ARect.Left + h - w, ARect.Top, ARect.Left + h + w, ARect.Bottom);
    end;
  end;
end;

function TTMSFNCCustomTrackBarBase.GetTrackLineRect: TRectF;
var
  br, cr: TRectF;
  bw, bh: Single;
begin
  br := GetMaxButtonRect;
  cr := GetContentRect;
  bw := br.Right - br.Left;
  bh := br.Bottom - br.Top;
  case FAppearance.Orientation of
    tboHorizontal: Result := RectF(cr.Left + bw + SLIDERLINESPACING, cr.Top, cr.Right - bw - SLIDERLINESPACING, cr.Bottom);
    tboVertical: Result := RectF(cr.Left, cr.Top + bh + SLIDERLINESPACING, cr.Right, cr.Bottom - bh - SLIDERLINESPACING);
  end;
end;

procedure TTMSFNCCustomTrackBarBase.HandleMouseDown(Button: TTMSFNCMouseButton;
  Shift: TShiftState; X, Y: Single);
var
  rl, rr: TRectF;
  {$IFDEF FMXMOBILE}
  d: Single;
  {$ENDIF}
begin
  inherited;

  rl := GetSliderRect(True);
  rr := GetSliderRect(False);
  {$IFDEF FMXMOBILE}
  d := rl.Right - rl.Left / 2;
  if d <= 10 then
    rl := RectF(rl.Left - 10, rl.Top - 10, rl.Right + d, rl.Bottom + 10)
  else
    InflateRectEx(rl, 10, 10);
  {$ENDIF}
  if PtInRectEx(rl, PointF(X, Y)) then
  begin
    CaptureEx;
    FMouseDown := True;
    FLeftSlider := True;
    FPrevValue := FValues.Left;
    Invalidate;
    Exit;
  end;

  rr := GetSliderRect(False);
  {$IFDEF FMXMOBILE}
  if d <= 10 then
    rl := RectF(rr.Left - d, rr.Top - 10, rr.Right + 10, rr.Bottom + 10)
  else
    InflateRectEx(rr, 10, 10);
  {$ENDIF}
  if PtInRectEx(rr, PointF(X, Y)) then
  begin
    CaptureEx;
    FMouseDown := True;
    FRightSlider := True;
    FPrevValue := FValues.Right;
  end;

  Invalidate;
end;

procedure TTMSFNCCustomTrackBarBase.HandleMouseMove(Shift: TShiftState; X,
  Y: Single);
begin
  inherited;
  if not FMouseDown then
    Exit;

  InternalValueCheck(X, Y, True);
end;

procedure TTMSFNCCustomTrackBarBase.HandleMouseUp(Button: TTMSFNCMouseButton;
  Shift: TShiftState; X, Y: Single);
begin
  inherited;

  InternalValueCheck(X, Y, False);

  FMouseDown := False;
  FPlusDown := False;
  FMinusDown := False;
  FLeftSlider := False;
  FRightSlider := False;
  ReleaseCaptureEx;
  Invalidate;
end;

procedure TTMSFNCCustomTrackBarBase.InteractionChanged(Sender: TObject);
begin
  DoChanged;
end;

procedure TTMSFNCCustomTrackBarBase.InternalValueCheck(X, Y: Single;
  AChange: Boolean);
var
  v, tmp: Single;
begin
  if FLeftSlider then
  begin
    v := CalcValue(X, Y, True);
    if FValues.Left < v then
    begin
      if (v > FValues.Left + FInteraction.Frequency) and (FValues.Left + FInteraction.Frequency < FValues.Right) then
      begin
        tmp := Trunc(v / FInteraction.Frequency) * FInteraction.Frequency;
        FValues.Left := ClosestTo(v, tmp, tmp + 1);
      end
      else
        FValues.Left := ClosestTo(v, FValues.Left, FValues.Left + FInteraction.Frequency);

      if AChange then
      begin
        DoValuesChange(FValues.Left, True);
        DoChanged;
      end
      else if FPrevValue <> FValues.Left then
      begin
        DoValuesChanged(FValues.Left, True);
        DoChanged;
      end;
    end
    else if FValues.Left >= v then
    begin
      if (v < FValues.Left - FInteraction.Frequency) and (FValues.Left - FInteraction.Frequency > FMin) then
      begin
        tmp := Trunc(v / FInteraction.Frequency) * FInteraction.Frequency;
        FValues.Left := ClosestTo(v, tmp, tmp + 1);
      end
      else
        FValues.Left := ClosestTo(v, FValues.Left, FValues.Left - FInteraction.Frequency);

      if AChange then
      begin
        DoValuesChange(FValues.Left, True);
        DoChanged;
      end
      else if FPrevValue <> FValues.Left then
      begin
        DoValuesChanged(FValues.Left, True);
        DoChanged;
      end;
    end;
    Exit;
  end;

  if FRightSlider then
  begin
    v := CalcValue(X, Y, False);
    if FValues.Right < v then
    begin
      if (v > FValues.Right + FInteraction.Frequency) and (FValues.Right + FInteraction.Frequency < FMax) then
      begin
        tmp := Trunc(v / FInteraction.Frequency) * FInteraction.Frequency;
        FValues.Right := ClosestTo(v, tmp, tmp + 1);
      end
      else
        FValues.Right := ClosestTo(v, FValues.Right, FValues.Right + FInteraction.Frequency);

      if AChange then
      begin
        DoValuesChange(FValues.Right, False);
        DoChanged;
      end
      else if FPrevValue <> FValues.Right then
      begin
        DoValuesChanged(FValues.Right, False);
        DoChanged;
      end;
    end
    else if FValues.Right > v then
    begin
      if (v < FValues.Right - FInteraction.Frequency) and (FValues.Right - FInteraction.Frequency > FValues.Left) then
      begin
        tmp := Trunc(v / FInteraction.Frequency) * FInteraction.Frequency;
        FValues.Right := ClosestTo(v, tmp, tmp + 1);
      end
      else
        FValues.Right := ClosestTo(v, FValues.Right, FValues.Right - FInteraction.Frequency);

      if AChange then
      begin
        DoValuesChange(FValues.Right, False);
        DoChanged;
      end
      else if FPrevValue <> FValues.Right then
      begin
        DoValuesChanged(FValues.Right, False);
        DoChanged;
      end;
    end;
  end;
end;

function TTMSFNCCustomTrackBarBase.IsMaxStored: Boolean;
begin
  Result := FMax <> 100.0;
end;

function TTMSFNCCustomTrackBarBase.IsMinStored: Boolean;
begin
  Result := FMin <> 0.0;
end;

procedure TTMSFNCCustomTrackBarBase.ResetToDefaultStyle;
begin
  inherited;
  Fill.Color := gcWhite;
  FAppearance.LineFill.Color := gcWhite;
  FAppearance.ButtonFill.Color := gcWhite;
  FAppearance.ThumbFill.Color := gcWhite;
  FAppearance.ThumbRightFill.Color := gcWhite;
  Stroke.Kind := gskSolid;
  FAppearance.LineStroke.Color := gcSilver;
  FAppearance.ButtonStroke.Color := gcSilver;
  FAppearance.ThumbStroke.Color := gcSilver;
  FAppearance.ThumbDownStroke.Color := gcSilver;
  FAppearance.ThumbRightStroke.Color := gcSilver;
  FAppearance.ThumbRightDownStroke.Color := gcSilver;
  FAppearance.TickMarkStroke.Color := gcSilver;
  FAppearance.ButtonDownFill.Color := gcWhite;
  FAppearance.ThumbDownFill.Color := gcWhite;
  FAppearance.ThumbRightDownFill.Color := gcWhite;
  FAppearance.ValueFont.Color := gcBlack;
end;

procedure TTMSFNCCustomTrackBarBase.SetAppearance(
  const AValue: TTMSFNCCustomTrackBarBaseAppearance);
begin
  FAppearance.Assign(AValue);
end;

{$IFDEF FMXLIB}
procedure TTMSFNCCustomTrackBarBase.SetEnabled(const Value: Boolean);
{$ENDIF}
{$IFDEF CMNWEBLIB}
procedure TTMSFNCCustomTrackBarBase.SetEnabled(Value: Boolean);
{$ENDIF}
begin
  inherited;
  if Value then
  begin
    Appearance.ButtonStroke.Color := FOldButtonColor;
    Appearance.ThumbStroke.Color := FOldThumbColor;
    Appearance.ThumbRightStroke.Color := FOldThumbRightColor;
    Appearance.LineStroke.Color := FOldLineColor;
    Appearance.TickMarkStroke.Color := FOldTickMarkColor;
    Appearance.ButtonDownStroke.Color := FOldButtonDownColor;
    Appearance.ThumbDownStroke.Color := FOldThumbDownColor;
    Appearance.ThumbRightDownStroke.Color := FOldThumbRightDownColor;
    Appearance.ButtonFill.Color := FOldButtonFill;
    Appearance.ThumbFill.Color := FOldThumbFill;
    Appearance.ThumbRightFill.Color := FOldThumbRightFill;
    Appearance.LineFill.Color := FOldLineFill;
    Appearance.ButtonDownFill.Color := FOldButtonDownFill;
    Appearance.ThumbDownFill.Color := FOldThumbDownFill;
    Appearance.ThumbRightDownFill.Color := FOldThumbRightDownFill;
  end
  else
  begin
    FOldButtonColor := Appearance.ButtonStroke.Color;
    Appearance.ButtonStroke.Color := gcSilver;

    FOldThumbColor := Appearance.ThumbStroke.Color;
    Appearance.ThumbStroke.Color := gcSilver;

    FOldThumbRightColor := Appearance.ThumbRightStroke.Color;
    Appearance.ThumbRightStroke.Color := gcSilver;

    FOldLineColor := Appearance.LineStroke.Color;
    Appearance.LineStroke.Color := gcSilver;

    FOldTickMarkColor := Appearance.TickMarkStroke.Color;
    Appearance.TickMarkStroke.Color := gcSilver;

    FOldButtonDownColor := Appearance.ButtonDownStroke.Color;
    Appearance.ButtonDownStroke.Color := gcSilver;

    FOldThumbDownColor := Appearance.ThumbDownStroke.Color;
    Appearance.ThumbDownStroke.Color := gcSilver;

    FOldThumbRightDownColor := Appearance.ThumbRightDownStroke.Color;
    Appearance.ThumbRightDownStroke.Color := gcSilver;

    FOldButtonFill := Appearance.ButtonFill.Color;
    Appearance.ButtonFill.Color := MakeGraphicsColor(240, 240, 240);

    FOldThumbFill := Appearance.ThumbFill.Color;
    Appearance.ThumbFill.Color := MakeGraphicsColor(240, 240, 240);

    FOldThumbRightFill := Appearance.ThumbRightFill.Color;
    Appearance.ThumbRightFill.Color := MakeGraphicsColor(240, 240, 240);

    FOldLineFill := Appearance.LineFill.Color;
    Appearance.LineFill.Color := MakeGraphicsColor(240, 240, 240);

    FOldButtonDownFill := Appearance.ButtonDownFill.Color;
    Appearance.ButtonDownFill.Color := MakeGraphicsColor(240, 240, 240);

    FOldThumbDownFill := Appearance.ThumbDownFill.Color;
    Appearance.ThumbDownFill.Color := MakeGraphicsColor(240, 240, 240);

    FOldThumbRightDownFill := Appearance.ThumbRightDownFill.Color;
    Appearance.ThumbRightDownFill.Color := MakeGraphicsColor(240, 240, 240);
  end;
  Invalidate;
end;

procedure TTMSFNCCustomTrackBarBase.SetInteraction(
  const AValue: TTMSFNCCustomTrackBarBaseInteraction);
begin
  FInteraction.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBase.SetMax(const AValue: Single);
begin
  if FMax <> AValue then
  begin
    FMax := AValue;
    if FValues.Right > FMax then
      FValues.Right := FMax;

    DoChanged;
  end;
end;

procedure TTMSFNCCustomTrackBarBase.SetMin(const AValue: Single);
begin
  if FMin <> AValue then
  begin
    FMin := AValue;
    if FValues.Left < FMin then
    begin
      FValues.Left := FMin;
      DoValuesChange(FValues.Left, True);
      DoValuesChanged(FValues.Left, True)
    end;

    DoChanged;
  end;
end;

procedure TTMSFNCCustomTrackBarBase.SetValues(
  const AValue: TTMSFNCCustomTrackBarBaseValues);
begin
  FValues := AValue;
  DoChanged;
end;

{ TTMSFNCCustomTrackBarBaseAppearance }

procedure TTMSFNCCustomTrackBarBaseAppearance.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCCustomTrackBarBaseAppearance then
  begin
    FButtonFill.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ButtonFill);
    FButtonStroke.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ButtonStroke);
    FButtonSize := (Source as TTMSFNCCustomTrackBarBaseAppearance).ButtonSize;
    FButtonSpacing := (Source as TTMSFNCCustomTrackBarBaseAppearance).ButtonSpacing;
    FThumbFill.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbFill);
    FThumbStroke.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbStroke);
    FThumbHeight := (Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbHeight;
    FThumbWidth := (Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbWidth;
    FThumbRightHeight := (Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbRightHeight;
    FThumbRightWidth := (Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbRightWidth;
    FThumbRightFill.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbRightFill);
    FThumbRightStroke.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbRightStroke);
    FLineWidth := (Source as TTMSFNCCustomTrackBarBaseAppearance).LineWidth;
    FLineShape := (Source as TTMSFNCCustomTrackBarBaseAppearance).LineShape;
    FLineFill.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).LineFill);
    FLineStroke.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).LineStroke);
    FTickMarkSize := (Source as TTMSFNCCustomTrackBarBaseAppearance).TickMarkSize;
    FTickMarkSpacing := (Source as TTMSFNCCustomTrackBarBaseAppearance).TickMarkSpacing;
    FTickMarkStroke.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).TickMarkStroke);
    FButtonShape := (Source as TTMSFNCCustomTrackBarBaseAppearance).ButtonShape;
    FTickMarkPosition := (Source as TTMSFNCCustomTrackBarBaseAppearance).TickMarkPosition;
    FValueFont.AssignSource((Source as TTMSFNCCustomTrackBarBaseAppearance).ValueFont);
    FThumbShape := (Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbShape;
    FTickMarkLabelFormat := (Source as TTMSFNCCustomTrackBarBaseAppearance).TickMarkLabelFormat;
    FTrackLabelFormat := (Source as TTMSFNCCustomTrackBarBaseAppearance).TrackLabelFormat;
    FTrackLabelPosition := (Source as TTMSFNCCustomTrackBarBaseAppearance).TrackLabelPosition;
    FTickMarkLabel := (Source as TTMSFNCCustomTrackBarBaseAppearance).TickMarkLabel;
    FTickMarkDivision := (Source as TTMSFNCCustomTrackBarBaseAppearance).TickMarkDivision;
    FOrientation := (Source as TTMSFNCCustomTrackBarBaseAppearance).Orientation;
    FThumbDownFill.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbDownFill);
    FThumbDownStroke.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbDownStroke);
    FThumbRightDownFill.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbRightDownFill);
    FThumbRightDownStroke.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbRightDownStroke);
    FButtonDownFill.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ButtonDownFill);
    FButtonDownStroke.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ButtonDownStroke);
    FMinButtonBitmap.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).MinButtonBitmap);
    FMinButtonDownBitmap.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).MinButtonDownBitmap);
    FPlusButtonBitmap.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).PlusButtonBitmap);
    FPlusButtonDownBitmap.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).PlusButtonDownBitmap);
    FLineBitmap.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).LineBitmap);
    FThumbBitmap.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbBitmap);
    FThumbDownBitmap.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbDownBitmap);
    FThumbRightBitmap.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbRightBitmap);
    FThumbRightDownBitmap.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).ThumbRightDownBitmap);
    FTickMarkLabelFont.AssignSource((Source as TTMSFNCCustomTrackBarBaseAppearance).TickMarkLabelFont);
    FTrackLabelFont.AssignSource((Source as TTMSFNCCustomTrackBarBaseAppearance).TrackLabelFont);
    FLineRightFill.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).LineRightFill);
    FLineRightStroke.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).LineRightStroke);
    FLineMiddleFill.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).LineMiddleFill);
    FLineMiddleStroke.Assign((Source as TTMSFNCCustomTrackBarBaseAppearance).LineMiddleStroke);
  end
  else
    inherited;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.BitmapChanged(Sender: TObject);
begin
  Changed;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.Changed;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

constructor TTMSFNCCustomTrackBarBaseAppearance.Create(AOwner: TComponent);
begin
  FOwner := AOwner;
  FButtonFill := TTMSFNCGraphicsFill.Create;
  FButtonFill.OnChanged := @FillChanged;
  FButtonStroke := TTMSFNCGraphicsStroke.Create(gskSolid, gcGray);
  FButtonStroke.OnChanged := @StrokeChanged;
  FButtonSize := 20;
  FButtonSpacing := 2;
  FThumbFill := TTMSFNCGraphicsFill.Create;
  FThumbFill.OnChanged := @FillChanged;
  FThumbStroke := TTMSFNCGraphicsStroke.Create(gskSolid, gcGray);
  FThumbStroke.OnChanged := @StrokeChanged;
  FThumbHeight := 16;
  FThumbWidth := 8;
  FThumbRightFill := TTMSFNCGraphicsFill.Create;
  FThumbRightFill.OnChanged := @FillChanged;
  FThumbRightStroke := TTMSFNCGraphicsStroke.Create(gskSolid, gcGray);
  FThumbRightStroke.OnChanged := @StrokeChanged;
  FThumbRightHeight := 16;
  FThumbRightWidth := 8;
  FLineShape := lsRectangle;
  FLineWidth := 1;
  FLineFill := TTMSFNCGraphicsFill.Create;
  FLineFill.OnChanged := @FillChanged;
  FLineStroke := TTMSFNCGraphicsStroke.Create(gskSolid, gcGray);
  FLineStroke.OnChanged := @StrokeChanged;
  FTickMarkSize := 4;
  FTickMarkSpacing := 8;
  FTickMarkStroke := TTMSFNCGraphicsStroke.Create(gskSolid, gcGray);
  FTickMarkStroke.OnChanged := @StrokeChanged;
  FButtonShape := bsNoButton;
  FButtonDownFill := TTMSFNCGraphicsFill.Create;
  FButtonDownFill.OnChanged := @FillChanged;
  FButtonDownStroke := TTMSFNCGraphicsStroke.Create(gskSolid, gcGray);
  FButtonDownStroke.OnChanged := @StrokeChanged;
  FThumbDownFill := TTMSFNCGraphicsFill.Create;
  FThumbDownFill.OnChanged := @FillChanged;
  FThumbDownStroke:= TTMSFNCGraphicsStroke.Create(gskSolid, gcGray);
  FThumbDownStroke.OnChanged := @StrokeChanged;
  FThumbRightDownFill := TTMSFNCGraphicsFill.Create;
  FThumbRightDownFill.OnChanged := @FillChanged;
  FThumbRightDownStroke := TTMSFNCGraphicsStroke.Create(gskSolid, gcGray);
  FThumbRightDownStroke.OnChanged := @StrokeChanged;
  FTickMarkLabelFont := TTMSFNCGraphicsFont.Create;
  FTickMarkLabelFont.OnChanged := @FontChanged;
  FTickMarkPosition := tmpBottomRight;
  FValueFont := TTMSFNCGraphicsFont.Create;
  FValueFont.OnChanged := @FontChanged;
  FTrackLabelFont := TTMSFNCGraphicsFont.Create;
  FTrackLabelFont.OnChanged := @FontChanged;
  FThumbShape := tsRectangle;
  FTickMarkLabelFormat := '%g';
  FTrackLabelFormat := 'Pos: %g';
  FTrackLabelPosition := tlpNone;
  FTickMarkLabel := False;
  FTickMarkDivision := 5;
  FOrientation := tboHorizontal;
  FMinButtonBitmap := TTMSFNCBitmap.Create;
  FMinButtonBitmap.OnChange := @BitmapChanged;
  FMinButtonDownBitmap := TTMSFNCBitmap.Create;
  FMinButtonDownBitmap.OnChange := @BitmapChanged;
  FPlusButtonBitmap := TTMSFNCBitmap.Create;
  FPlusButtonBitmap.OnChange := @BitmapChanged;
  FPlusButtonDownBitmap := TTMSFNCBitmap.Create;
  FPlusButtonDownBitmap.OnChange := @BitmapChanged;
  FLineBitmap := TTMSFNCBitmap.Create;
  FLineBitmap.OnChange := @BitmapChanged;
  FThumbBitmap := TTMSFNCBitmap.Create;
  FThumbBitmap.OnChange := @BitmapChanged;
  FThumbDownBitmap := TTMSFNCBitmap.Create;
  FThumbDownBitmap.OnChange := @BitmapChanged;
  FThumbRightBitmap := TTMSFNCBitmap.Create;
  FThumbRightBitmap.OnChange := @BitmapChanged;
  FThumbRightDownBitmap := TTMSFNCBitmap.Create;
  FThumbRightDownBitmap.OnChange := @BitmapChanged;
  FLineRightStroke := TTMSFNCGraphicsStroke.Create;
  FLineRightStroke.OnChanged := @StrokeChanged;
  FLineRightFill := TTMSFNCGraphicsFill.Create;
  FLineRightFill.OnChanged := @FillChanged;
  FLineMiddleStroke := TTMSFNCGraphicsStroke.Create;
  FLineMiddleStroke.OnChanged := @StrokeChanged;
  FLineMiddleFill := TTMSFNCGraphicsFill.Create;
  FLineMiddleFill.OnChanged := @FillChanged;
end;

destructor TTMSFNCCustomTrackBarBaseAppearance.Destroy;
begin
  FButtonFill.Free;
  FButtonStroke.Free;
  FThumbFill.Free;
  FThumbStroke.Free;
  FThumbRightFill.Free;
  FThumbRightStroke.Free;
  FLineFill.Free;
  FLineStroke.Free;
  FTickMarkStroke.Free;
  FButtonDownFill.Free;
  FButtonDownStroke.Free;
  FThumbDownFill.Free;
  FThumbDownStroke.Free;
  FThumbRightDownFill.Free;
  FThumbRightDownStroke.Free;
  FValueFont.Free;
  FMinButtonBitmap.Free;
  FMinButtonDownBitmap.Free;
  FPlusButtonBitmap.Free;
  FPlusButtonDownBitmap.Free;
  FLineBitmap.Free;
  FThumbBitmap.Free;
  FThumbDownBitmap.Free;
  FThumbRightBitmap.Free;
  FThumbRightDownBitmap.Free;
  FTickMarkLabelFont.Free;
  FTrackLabelFont.Free;
  FLineRightFill.Free;
  FLineRightStroke.Free;
  FLineMiddleStroke.Free;
  FLineMiddleFill.Free;
  inherited;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.FillChanged(Sender: TObject);
begin
  Changed;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.FontChanged(Sender: TObject);
begin
  Changed;
end;

function TTMSFNCCustomTrackBarBaseAppearance.IsTickMarkLabelFormatStored: Boolean;
begin
  Result := FTickMarkLabelFormat <> '%g';
end;

function TTMSFNCCustomTrackBarBaseAppearance.IsTrackLabelFormatStored: Boolean;
begin
  Result := FTrackLabelFormat <> 'Pos: %g';
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetPlusButtonBitmap(
  const Value: TTMSFNCBitmap);
begin
  FPlusButtonBitmap.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetMinButtonBitmap(
  const Value: TTMSFNCBitmap);
begin
  FMinButtonBitmap.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetMinButtonDownBitmap(
  const Value: TTMSFNCBitmap);
begin
  FMinButtonDownBitmap.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetButtonDownFill(
  const AValue: TTMSFNCGraphicsFill);
begin
  FButtonDownFill.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetButtonDownStroke(
  const AValue: TTMSFNCGraphicsStroke);
begin
  FButtonDownStroke.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetButtonFill(
  const AValue: TTMSFNCGraphicsFill);
begin
  FButtonFill.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetButtonShape(
  const AValue: TTMSFNCTrackBarAppearanceButtonShape);
begin
  if FButtonShape <> AValue then
  begin
    FButtonShape := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetButtonSize(const AValue: Integer);
begin
  if FButtonSize <> AValue then
  begin
    FButtonSize := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetButtonSpacing(
  const AValue: Integer);
begin
  if FButtonSpacing <> AValue then
  begin
    FButtonSpacing := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetButtonStroke(
  const AValue: TTMSFNCGraphicsStroke);
begin
  FButtonStroke.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetLineBitmap(
  const Value: TTMSFNCBitmap);
begin
  FLineBitmap.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetLineFill(
  const AValue: TTMSFNCGraphicsFill);
begin
  FLineFill.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetLineMiddleFill(
  const Value: TTMSFNCGraphicsFill);
begin
  FLineMiddleFill.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetLineMiddleStroke(
  const Value: TTMSFNCGraphicsStroke);
begin
  FLineMiddleStroke.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetLineRightStroke(
  const Value: TTMSFNCGraphicsStroke);
begin
  FLineRightStroke.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetLineRightFill(
  const Value: TTMSFNCGraphicsFill);
begin
  FLineRightFill.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetLineShape(
  const Value: TTMSFNCCustomTrackBarBaseLineShape);
begin
  if FLineShape <> Value then
  begin
    FLineShape := Value;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetLineStroke(
  const AValue: TTMSFNCGraphicsStroke);
begin
  FLineStroke.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetLineWidth(
  const AValue: Integer);
begin
  if FLineWidth <> AValue then
  begin
    FLineWidth := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetOrientation(
  const AValue: TTMSFNCCustomTrackBarOrientation);
begin
  if FOrientation <> AValue then
  begin
    FOrientation := AValue;
    if Assigned(FOwner) and (FOwner is TTMSFNCCustomTrackBarBase) then
    begin
      if FOwner.ComponentState * [csLoading, csUpdating] = [] then
      begin
      {$IFDEF FMXLIB}
      (FOwner as TTMSFNCCustomTrackBarBase).SetBounds((FOwner as TTMSFNCCustomTrackBarBase).Position.x, (FOwner as TTMSFNCCustomTrackBarBase).Position.Y, (FOwner as TTMSFNCCustomTrackBarBase).Height, (FOwner as TTMSFNCCustomTrackBarBase).Width);
      {$ENDIF}
      {$IFNDEF FMXLIB}
      (FOwner as TTMSFNCCustomTrackBarBase).SetBounds((FOwner as TTMSFNCCustomTrackBarBase).Left, (FOwner as TTMSFNCCustomTrackBarBase).Top, (FOwner as TTMSFNCCustomTrackBarBase).Height, (FOwner as TTMSFNCCustomTrackBarBase).Width);
      {$ENDIF}
      end;
    end;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetPlusButtonDownBitmap(
  const Value: TTMSFNCBitmap);
begin
  FPlusButtonDownBitmap.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbBitmap(
  const Value: TTMSFNCBitmap);
begin
  FThumbBitmap.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbDownBitmap(
  const Value: TTMSFNCBitmap);
begin
  FThumbDownBitmap.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbDownFill(
  const AValue: TTMSFNCGraphicsFill);
begin
  FThumbDownFill.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbDownStroke(
  const AValue: TTMSFNCGraphicsStroke);
begin
  FThumbDownStroke.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbFill(
  const AValue: TTMSFNCGraphicsFill);
begin
  FThumbFill.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbHeight(
  const AValue: Integer);
begin
  if FThumbHeight <> AValue then
  begin
    FThumbHeight := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbRightBitmap(
  const Value: TTMSFNCBitmap);
begin
  FThumbRightBitmap.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbRightDownBitmap(
  const Value: TTMSFNCBitmap);
begin
  FThumbRightDownBitmap.Assign(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbRightDownFill(
  const AValue: TTMSFNCGraphicsFill);
begin
  FThumbRightDownFill.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbRightDownStroke(
  const AValue: TTMSFNCGraphicsStroke);
begin
  FThumbRightDownStroke.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbRightFill(
  const AValue: TTMSFNCGraphicsFill);
begin
  FThumbRightFill.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbRightHeight(
  const AValue: Integer);
begin
  if FThumbRightHeight <> AValue then
  begin
    FThumbRightHeight := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbRightStroke(
  const AValue: TTMSFNCGraphicsStroke);
begin
  FThumbRightStroke.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbRightWidth(
  const AValue: Integer);
begin
  if FThumbRightWidth <> AValue then
  begin
    FThumbRightWidth := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbShape(
  const AValue: TTMSFNCCustomTrackBarBaseShape);
begin
  if FThumbShape <> AValue then
  begin
    FThumbShape := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbStroke(
  const AValue: TTMSFNCGraphicsStroke);
begin
  FThumbStroke.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetThumbWidth(
  const AValue: Integer);
begin
  if FThumbWidth <> AValue then
  begin
    FThumbWidth := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetTickMarkDivision(
  const AValue: Integer);
begin
  if FTickMarkDivision <> AValue then
  begin
    FTickMarkDivision := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetTickMarkLabel(
  const AValue: Boolean);
begin
  if FTickMarkLabel <> AValue then
  begin
    FTickMarkLabel := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetTickMarkLabelFont(
  const Value: TTMSFNCGraphicsFont);
begin
  FTickMarkLabelFont.AssignSource(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetTickMarkLabelFormat(
  const AValue: string);
begin
  if FTickMarkLabelFormat <> AValue then
  begin
    FTickMarkLabelFormat := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetTickMarkPosition(
  const AValue: TTMSFNCTrackBarAppearanceTickMarkPosition);
begin
  if FTickMarkPosition <> AValue then
  begin
    FTickMarkPosition := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetTickMarkSize(
  const AValue: Integer);
begin
  if FTickMarkSize <> AValue then
  begin
    FTickMarkSize := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetTickMarkSpacing(
  const AValue: Integer);
begin
  if FTickMarkSpacing <> AValue then
  begin
    FTickMarkSpacing := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetTickMarkStroke(const AValue: TTMSFNCGraphicsStroke);
begin
  FTickMarkStroke.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetTrackLabelFont(const Value: TTMSFNCGraphicsFont);
begin
  FTrackLabelFont.AssignSource(Value);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetTrackLabelFormat(const AValue: string);
begin
  if FTrackLabelFormat <> AValue then
  begin
    FTrackLabelFormat := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetTrackLabelPosition(const AValue: TTMSFNCCustomTrackBarTrackLabelPosition);
begin
  if FTrackLabelPosition <> AValue then
  begin
    FTrackLabelPosition := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.SetValueFont(const AValue: TTMSFNCGraphicsFont);
begin
  FValueFont.AssignSource(AValue);
end;

procedure TTMSFNCCustomTrackBarBaseAppearance.StrokeChanged(Sender: TObject);
begin
  Changed;
end;

{ TTMSFNCCustomTrackBarBaseInteraction }

procedure TTMSFNCCustomTrackBarBaseInteraction.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCCustomTrackBarBaseInteraction then
  begin
    FFrequency := (Source as TTMSFNCCustomTrackBarBaseInteraction).Frequency;
    FEditable := (Source as TTMSFNCCustomTrackBarBaseInteraction).Editable;
    FRepeatClick := (Source as TTMSFNCCustomTrackBarBaseInteraction).RepeatClick;
    FRepeatInterval := (Source as TTMSFNCCustomTrackBarBaseInteraction).RepeatInterval;
    FJumpToExact := (Source as TTMSFNCCustomTrackBarBaseInteraction).JumpToExactValue;
  end
  else
    inherited;
end;

procedure TTMSFNCCustomTrackBarBaseInteraction.Changed;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

constructor TTMSFNCCustomTrackBarBaseInteraction.Create(AOwner: TComponent);
begin
  FOwner := AOwner;
  FFrequency := 1;
  FEditable := True;
  FRepeatClick := False;
  FJumpToExact := False;
  FRepeatInterval := 100;
end;

function TTMSFNCCustomTrackBarBaseInteraction.IsFrequencyStored: Boolean;
begin
  Result := FFrequency <> 1.0;
end;

procedure TTMSFNCCustomTrackBarBaseInteraction.SetFrequency(const AValue: Single);
begin
  if FFrequency <> AValue then
  begin
    FFrequency := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseInteraction.SetJumpToExact(
  const Value: Boolean);
begin
  if FJumpToExact <> Value then
  begin
    FJumpToExact := Value;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseInteraction.SetEditable(
  const AValue: Boolean);
begin
  if FEditable <> AValue then
  begin
    FEditable := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseInteraction.SetRepeatClick(
  const AValue: Boolean);
begin
  if FRepeatClick <> AValue then
  begin
    FRepeatClick := AValue;
    Changed;
  end;
end;

procedure TTMSFNCCustomTrackBarBaseInteraction.SetRepeatInterval(
  const AValue: Integer);
begin
  if FRepeatInterval <> AValue then
  begin
    FRepeatInterval := AValue;
    Changed;
  end;
end;

{ TTMSFNCCustomTrackBar }

procedure TTMSFNCCustomTrackBar.ChangeDPIScale(M, D: Integer);
begin
  inherited;
  BeginUpdate;
  Appearance.TickMarkLabelFont.Height := TTMSFNCUtils.MulDivInt(Appearance.TickMarkLabelFont.Height, M, D);
  Appearance.TrackLabelFont.Height := TTMSFNCUtils.MulDivInt(Appearance.TrackLabelFont.Height, M, D);
  Appearance.ButtonSize := TTMSFNCUtils.MulDivInt(Appearance.ButtonSize, M, D);
  Appearance.ButtonSpacing := TTMSFNCUtils.MulDivInt(Appearance.ButtonSpacing, M, D);
  Appearance.ThumbHeight := TTMSFNCUtils.MulDivInt(Appearance.ThumbHeight, M, D);
  Appearance.ThumbWidth := TTMSFNCUtils.MulDivInt(Appearance.ThumbWidth, M, D);
  Appearance.TickMarkSize := TTMSFNCUtils.MulDivInt(Appearance.TickMarkSize, M, D);
  Appearance.TickMarkSpacing := TTMSFNCUtils.MulDivInt(Appearance.TickMarkSpacing, M, D);
  if Appearance.LineWidth > 1 then
    Appearance.LineWidth := TTMSFNCUtils.MulDivInt(Appearance.LineWidth, M, D);
  EndUpdate;
end;

constructor TTMSFNCCustomTrackBar.Create(AOwner: TComponent);
begin
  inherited;
  Width := 150;
  Height := 35;
  FValues.Left := 0.0;
  FValues.Right := FMax;
  FTrackInteraction := TTMSFNCCustomTrackBarInteraction.Create(Self);
  FTrackInteraction.OnChange := @TrackInteractionChange;
  FTrackAppearance := TTMSFNCCustomTrackBarAppearance.Create(Self);
  FTrackAppearance.OnChange := @TrackAppearanceChange;
  FRepeatTimer := TTimer.Create(Self);
  FRepeatTimer.Interval := FInteraction.RepeatInterval;
  FRepeatTimer.Enabled := False;
  FRepeatTimer.OnTimer := @DoRepeatTimer;

  FGlobalFont := TTMSFNCAppearanceGlobalFont.Create(Self);
end;

procedure TTMSFNCCustomTrackBar.DecreaseByFrequency;
var
  minv: Single;
begin
  minv := FValues.Left - FInteraction.Frequency;
  if minv < FMin then
    minv := FMin;

  if FValues.Left <> minv then
  begin
    FValues.Left := minv;
    DoValuesChange(FValues.Left, True);
    DoValuesChanged(FValues.Left, True);
    DoChanged;
  end;
end;

destructor TTMSFNCCustomTrackBar.Destroy;
begin
  FGlobalFont.Free;
  FTrackAppearance.Free;
  FTrackInteraction.Free;
  FRepeatTimer.Free;
  inherited;
end;

procedure TTMSFNCCustomTrackBar.DoAfterDrawThumb(AGraphics: TTMSFNCGraphics;
  ARect: TRectF; AValue: Single);
begin
  if Assigned(OnAfterDrawThumb) then
    OnAfterDrawThumb(Self, AGraphics, ARect, AValue);
end;

procedure TTMSFNCCustomTrackBar.DoBeforeDrawThumb(AGraphics: TTMSFNCGraphics;
  ARect: TRectF; AValue: Single; var AAllow, ADefaultDraw: Boolean);
begin
  if Assigned(OnBeforeDrawThumb) then
    OnBeforeDrawThumb(Self, AGraphics, ARect, AValue, AAllow, ADefaultDraw);
end;

procedure TTMSFNCCustomTrackBar.DoRepeatTimer(Sender: TObject);
begin
  if FPlusDown or (FTrackDown and (FToValue > FValues.Left)) then
    EncreaseByFrequency
  else if FMinusDown or (FTrackDown and (FToValue < FValues.Left)) then
    DecreaseByFrequency;
end;

procedure TTMSFNCCustomTrackBar.DoValueChange(AValue: Single);
begin
  if Assigned(OnValueChange) then
    OnValueChange(Self, AValue);
end;

procedure TTMSFNCCustomTrackBar.DoValueChanged(AValue: Single);
begin
  if Assigned(OnValueChanged) then
    OnValueChanged(Self, AValue);
end;

procedure TTMSFNCCustomTrackBar.DoValuesChange(AValue: Single; ALeft: Boolean);
begin
  inherited;

  if ALeft then
    DoValueChange(AValue);
end;

procedure TTMSFNCCustomTrackBar.DoValuesChanged(AValue: Single;
  ALeft: Boolean);
begin
  inherited;

  if ALeft then
    DoValueChanged(AValue);
end;

procedure TTMSFNCCustomTrackBar.DrawTrackLine(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
var
  leftR, rightR, sr: TRectF;
  b, df: Boolean;
begin
  inherited;
  sr := GetSliderRect(True);
  case FAppearance.Orientation of
    tboHorizontal:
    begin
      leftR := RectF(ARect.Left, ARect.Top, sr.Left + (sr.Right - sr.Left) / 2, ARect.Bottom);
      rightR := RectF(sr.Left + (sr.Right - sr.Left) / 2,  ARect.Top, ARect.Right, ARect.Bottom);
    end;
    tboVertical:
    begin
      leftR := RectF(ARect.Left, sr.Top + (sr.Bottom - sr.Top) / 2, ARect.Right, ARect.Bottom);
      rightR := RectF(ARect.Left, ARect.Top, ARect.Right, sr.Top + (sr.Bottom - sr.Top) / 2);
    end;
  end;

  AGraphics.Fill.Assign(FAppearance.LineFill);
  AGraphics.Stroke.Assign(FAppearance.LineStroke);

  b := True;
  df := True;
  DoBeforeDrawTrackLine(AGraphics, ARect, b, df);
  if b then
  begin
    if df then
    begin
      case FAppearance.LineShape of
        lsRectangle: AGraphics.DrawRectangle(leftR);
        lsRoundedRectangle:
        begin
          case FAppearance.Orientation of
            tboHorizontal: AGraphics.DrawRoundRectangle(leftR, SLIDERLINEROUNDING, [gcTopLeft, gcBottomLeft]);
            tboVertical: AGraphics.DrawRoundRectangle(leftR, SLIDERLINEROUNDING, [gcBottomLeft, gcBottomRight]);
          end;
        end;
        lsImage: AGraphics.DrawBitmap(ARect, FAppearance.LineBitmap);
      end;
    end;

    DoAfterDrawTrackLine(AGraphics, ARect);
  end;

  if FAppearance.LineShape <> lsImage then
  begin
    AGraphics.Fill.Assign(FAppearance.LineRightFill);
    AGraphics.Stroke.Assign(FAppearance.LineRightStroke);

    b := True;
    df := True;
    DoBeforeDrawTrackLine(AGraphics, rightR, b, df);
    if b then
    begin
      if df then
      begin
        case FAppearance.LineShape of
          lsRectangle: AGraphics.DrawRectangle(rightR);
          lsRoundedRectangle:
          begin
            case FAppearance.Orientation of
              tboHorizontal: AGraphics.DrawRoundRectangle(rightR, SLIDERLINEROUNDING, [gcTopRight, gcBottomRight]);
              tboVertical: AGraphics.DrawRoundRectangle(rightR, SLIDERLINEROUNDING, [gcTopLeft, gcTopRight]);
            end;
          end;
        end;
      end;

      DoAfterDrawTrackLine(AGraphics, rightR);
    end;
  end;
end;

procedure TTMSFNCCustomTrackBar.DrawTrackSliders(AGraphics: TTMSFNCGraphics;
  ARect: TRectF);
var
  r: TRectF;
  b, df: Boolean;
  bmp: TTMSFNCBitmap;
begin
  inherited;
  r := GetSliderRect(True);

  if FLeftSlider then
  begin
    AGraphics.Fill.Assign(FAppearance.ThumbDownFill);
    AGraphics.Stroke.Assign(FAppearance.ThumbDownStroke);
    bmp := FAppearance.ThumbDownBitmap;
  end
  else
  begin
    AGraphics.Fill.Assign(FAppearance.ThumbFill);
    AGraphics.Stroke.Assign(FAppearance.ThumbStroke);
    bmp := FAppearance.ThumbBitmap;
  end;

  b := True;
  df := True;
  DoBeforeDrawThumb(AGraphics, r, FValues.Left, b, df);
  if b then
  begin
    if df then
    begin
      case FAppearance.ThumbShape of
        tsRectangle: AGraphics.DrawRectangle(r);
        tsEllipse: AGraphics.DrawEllipse(r);
        tsImage:
        begin
          if Assigned(bmp) then
            AGraphics.DrawBitmap(r, bmp);
        end;
      end;
    end;

    DoAfterDrawThumb(AGraphics, ARect, FValues.Left);
  end;
end;

procedure TTMSFNCCustomTrackBar.EncreaseByFrequency;
var
  maxv: Single;
begin
  maxv := FValues.Left + FInteraction.Frequency;
  if maxv > FMax then
    maxv := FMax;

  if FValues.Left <> maxv then
  begin
    FValues.Left := maxv;
    DoValuesChange(FValues.Left, True);
    DoValuesChanged(FValues.Left, True);
    DoChanged;
  end;
end;

function TTMSFNCCustomTrackBar.GetMaxButtonRect: TRectF;
var
  r, cr: TRectF;
  l: Single;
begin
  r := RectF(0, 0, 0, 0);
  cr := GetContentRect;
  if FAppearance.ButtonShape <> bsNoButton then
  begin
    case FAppearance.Orientation of
      tboHorizontal:
      begin
        r := RectF(cr.Right - FAppearance.ButtonSize, cr.Top, cr.Right, cr.Bottom);
        l := r.Bottom - r.Top;
        InflateRectEx(r, 0, (l - FAppearance.ButtonSize) / -2);
      end;
      tboVertical:
      begin
        r := RectF(cr.Left, cr.Top, cr.Right, cr.Top + FAppearance.ButtonSize);
        l := r.Right - r.Left;
        InflateRectEx(r, (l - FAppearance.ButtonSize) / -2, 0);
      end;
    end;
  end;
  Result := r;
end;

function TTMSFNCCustomTrackBar.GetMaxOfThumbAndTrackRect: TRectF;
var
  m: Integer;
  r: TRectF;
  cp: Single;
begin
  r := GetTrackLineDrawRect(GetTrackLineRect);
  m := Math.Max(FAppearance.ThumbHeight, FAppearance.LineWidth);
  case FAppearance.Orientation of
    tboHorizontal:
    begin
      cp := r.Top + FAppearance.LineWidth / 2;
      Result := RectF(r.Left, cp - m / 2, r.Right, cp + m / 2);
    end;
    tboVertical:
    begin
      cp := r.Left + FAppearance.LineWidth / 2;
      Result := RectF(cp - m / 2, r.Top, cp + m / 2, r.Bottom);
    end;
  end;
end;

function TTMSFNCCustomTrackBar.GetMinButtonRect: TRectF;
var
  r, cr: TRectF;
  l: Single;
begin
  r := RectF(0, 0, 0, 0);
  cr := GetContentRect;
  if FAppearance.ButtonShape <> bsNoButton then
  begin
    case FAppearance.Orientation of
      tboHorizontal:
      begin
        r := RectF(cr.Left, cr.Top, cr.Left + FAppearance.ButtonSize, cr.Bottom);
        l := r.Bottom - r.Top;
        InflateRectEx(r, 0, (l - FAppearance.ButtonSize) / -2);
      end;
      tboVertical:
      begin
        r := RectF(cr.Left, cr.Bottom - FAppearance.ButtonSize, cr.Right, cr.Bottom);
        l := r.Right - r.Left;
        InflateRectEx(r, (l - FAppearance.ButtonSize) / -2, 0);
      end;
    end;
  end;
  Result := r;
end;

function TTMSFNCCustomTrackBar.GetValue: Single;
begin
  Result := FValues.Left;
end;

function TTMSFNCCustomTrackBar.GetThumbWidth: Single;
begin
  Result := FAppearance.ThumbWidth;
end;

function TTMSFNCCustomTrackBar.GetTrackLineRect: TRectF;
begin
  Result := inherited;
  if FAppearance.ButtonShape <> bsNoButton then
  begin
    case FAppearance.Orientation of
      tboHorizontal: InflateRectEx(Result, -FAppearance.ButtonSpacing, 0);
      tboVertical: InflateRectEx(Result, 0, -FAppearance.ButtonSpacing);
    end;
  end;
end;

procedure TTMSFNCCustomTrackBar.HandleKeyDown(var Key: Word;
  Shift: TShiftState);
begin
  inherited;
  case Key of
    KEY_RIGHT, KEY_UP: EncreaseByFrequency;
    KEY_DOWN, KEY_LEFT: DecreaseByFrequency;
  end;
end;

procedure TTMSFNCCustomTrackBar.HandleMouseDown(Button: TTMSFNCMouseButton;
  Shift: TShiftState; X, Y: Single);
var
  r, tr: TRectF;
  v: Single;
begin
  inherited;

  tr := GetMaxOfThumbAndTrackRect;
  if PtInRectEx(tr, PointF(X, Y)) then
  begin
    FToValue := CalcValue(X, Y, True);
    v := Trunc(FToValue / FInteraction.Frequency) * FInteraction.Frequency;
    FToValue := ClosestTo(FToValue, v, v + 1);
    if Interaction.JumpToExactValue then
    begin
      Value := FToValue;
      CaptureEx;
      FMouseDown := True;
      FLeftSlider := True;
    end
    else
    begin
      if FToValue < FValues.Left then
        DecreaseByFrequency
      else if FToValue > FValues.Left then
        EncreaseByFrequency;

      if FInteraction.RepeatClick then
      begin
        CaptureEx;
        FRepeatTimer.Enabled := True;
        FTrackDown := True;
      end;
    end;
    Exit;
  end;

  r := GetMaxButtonRect;
  {$IFDEF FMXMOBILE}
  InflateRectEx(r, 10, 10);
  {$ENDIF}
  if PtInRectEx(r, PointF(X, Y)) then
  begin
    FPlusDown := True;
    CaptureEx;
    EncreaseByFrequency;

    FRepeatTimer.Interval := FInteraction.RepeatInterval;
    FRepeatTimer.Enabled := FInteraction.RepeatClick;
    Invalidate;
    Exit;
  end;

  r := GetMinButtonRect;
  {$IFDEF FMXMOBILE}
  InflateRectEx(r, 10, 10);
  {$ENDIF}
  if PtInRectEx(r, PointF(X, Y)) then
  begin
    FMinusDown := True;
    CaptureEx;
    DecreaseByFrequency;

    FRepeatTimer.Interval := FInteraction.RepeatInterval;
    FRepeatTimer.Enabled := FInteraction.RepeatClick;
  end;

  Invalidate;
end;

procedure TTMSFNCCustomTrackBar.HandleMouseMove(Shift: TShiftState; X,
  Y: Single);
var
  v: Single;
begin
  inherited;
  if FTrackDown and not Interaction.JumpToExactValue then
  begin
    FToValue := CalcValue(X, Y, True);
    v := Trunc(FToValue / FInteraction.Frequency) * FInteraction.Frequency;
    FToValue := ClosestTo(FToValue, v, v + 1);
  end;
end;

procedure TTMSFNCCustomTrackBar.HandleMouseUp(Button: TTMSFNCMouseButton;
  Shift: TShiftState; X, Y: Single);
begin
  inherited;
  FTrackDown := False;
  FRepeatTimer.Enabled := False;
end;

procedure TTMSFNCCustomTrackBar.HandleMouseWheel(Shift: TShiftState;
  WheelDelta: Integer; var Handled: Boolean);
begin
  inherited;

  if Handled then
    Exit;

  if WheelDelta > 0 then
    EncreaseByFrequency
  else
    DecreaseByFrequency;
end;

function TTMSFNCCustomTrackBar.IsValueStored: Boolean;
begin
  Result := FValues.Left <> 0.0;
end;

procedure TTMSFNCCustomTrackBar.SetFonts(ASetType: TTMSFNCAppearanceGlobalFontType);
begin
  BeginUpdate;

  GlobalFont.ApplyChange(Appearance.TickMarkLabelFont, ASetType);
  GlobalFont.ApplyChange(Appearance.TrackLabelFont, ASetType);
  GlobalFont.ApplyChange(Appearance.ValueFont, ASetType);

  EndUpdate;
end;

procedure TTMSFNCCustomTrackBar.SetGlobalFont(const Value: TTMSFNCAppearanceGlobalFont);
begin
  FGlobalFont.Assign(Value);
end;

procedure TTMSFNCCustomTrackBar.SetMax(const AValue: Single);
begin
  inherited;
  FValues.Right := AValue;
end;

procedure TTMSFNCCustomTrackBar.SetValue(const AValue: Single);
begin
  if FValues.Left <> AValue then
  begin
    FValues.Left := AValue;
    DoValuesChange(FValues.Left, True);
    DoValuesChanged(FValues.Left, True);
    DoChanged;
  end;
end;

procedure TTMSFNCCustomTrackBar.SetTrackAppearance(
  const AValue: TTMSFNCCustomTrackBarAppearance);
begin
  FTrackAppearance.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBar.SetTrackInteraction(
  const AValue: TTMSFNCCustomTrackBarInteraction);
begin
  FTrackInteraction.Assign(AValue);
end;

procedure TTMSFNCCustomTrackBar.TrackAppearanceChange(Sender: TObject);
begin
  FAppearance.Assign(FTrackAppearance);
end;

procedure TTMSFNCCustomTrackBar.TrackInteractionChange(Sender: TObject);
begin
  FInteraction.Assign(FTrackInteraction);
end;

end.
